import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import {
  X, Calendar, FileText, ExternalLink, Heart, ZoomIn, Maximize2, Minimize2,
  Clock, AlertCircle, CheckCircle, XCircle, Copy, Check, Download,
  Eye, Trash2, ChevronLeft, ChevronRight, Building, MessageSquareText,
  TrendingUp, FileSearch, Tags, Flag, MapPinned, Mail
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { getCategoryById } from '../../utils/projectCategories';
import CategoryIcon from './filters/CategoryIcons';
import ShareButton from '../ui/ShareButton';
import { apiOnlyCategorizeApplication } from '../../utils/categorizationHelper';
import { getPlanningDocumentUrl, isIdoxDocumentUrl, openPlanningDoc, openPrimedPlanningDoc } from '../../utils/openPlanningDoc';
import { usePlanningContext } from './state/PlanningContext';
import { trackViewWithCooldown, detectSource } from '../../lib/viewTracker';
import { createReport } from '../../lib/accountsReports';
import userDataService from '../../lib/services/userDataService';
import { applicationIdentityKey, formatCouncilQualifiedReference, getCouncilConfig } from '../../lib/councils';
import { isSourceOnlyPlanningRecord } from '../../lib/planning/londonSharedDelivery';
import { sanitizePublicPlanningRecord } from '../../lib/planning/publicReleasePolicy';
import { buildCityScopeUtmUrl, CITYSCOPE_INTERNAL_CTA_ATTRIBUTION } from '../../lib/analyticsAttribution';
import { trackCoreJourneyEvent } from '../../lib/coreJourneyAnalytics';
import { buildPlanningApplicationUrl } from '../../lib/planningApplicationUrl';
import { formatLongPlanningDate } from '../../lib/planningDates';
import ApplicationFormPanel from './ApplicationFormPanel';
import CouncilCoverageNotice from './CouncilCoverageNotice';
import CouncilDecisionFacts from './CouncilDecisionFacts';
import {
  buildFormDataSummary,
  extractInlineApplicationFormData,
  hasApplicationFormData,
  normalizeFormPayload
} from '../../lib/planning/formData';
import ApplicationInsightsCard from './ApplicationInsightsCard';
import { usePlanningApplicationDetailHydration } from '../../lib/planning/usePlanningApplicationDetailHydration';
import { londonDocumentListNotice, londonSourceRefreshNotice, londonReportedDecisionNotice } from '../../lib/services/londonPlanningService';
import { usePlanningDiscussionsFeatureFlag } from '../../lib/planningDiscussionFeatureFlag';
import PlanningDiscussionPanel from './discussions/PlanningDiscussionPanel';
import {
  getDiscussionPreviewComments,
  getDiscussionCount,
  subscribeToDiscussionUpdates,
} from '../../lib/services/planningDiscussionsService';
import PlanningSignalsPanel from './signals/PlanningSignalsPanel';
import PlanningSignalsEvidenceTab from './signals/PlanningSignalsEvidenceTab';
import { getDisplayPlanningSignals } from '../../lib/planning/tags';
import { renderInlineMarkdown } from '../blog/MarkdownInline';
import { getPlanningArticlePhotoItems, PlanningArticleInlinePhoto } from '../blog/PlanningArticlePhotos';
import { getMapSnapshotUrl, MAP_SNAPSHOT_ATTRIBUTION } from '../../lib/planning/mapSnapshotUrl';
import {
  fetchPlanningArticleByReference,
  parseMarkdownToBlogSections,
} from '../../lib/planningArticles';
import {
  PLANNING_APPLICATION_MODAL_ACTIONS,
  createPlanningApplicationModalAnalyticsSession,
  trackPlanningApplicationModalEvent,
} from '../../lib/planningApplicationModalAnalytics';
import { requestPlanningReport } from '../../lib/generateReportClient';
// Custom modal timeline rendering; not using the planning-updates timeline component

const PlanningArticleMap = dynamic(() => import('../blog/PlanningArticleMap'), {
  ssr: false,
  loading: () => (
    <div className="flex h-[min(58vh,620px)] min-h-[360px] items-center justify-center rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] text-sm text-[var(--c-text-secondary)]">
      Loading map...
    </div>
  ),
});

// Stable empty array to avoid creating a new [] on every render
const EMPTY_ARRAY = Object.freeze([]);
const FORM_CACHE_TTL_MS = 60 * 1000;
const ARTICLE_CACHE_TTL_MS = 5 * 60 * 1000;
const DETAIL_COMPANION_EXIT_MS = 320;

const getDocumentOpenUrl = (doc) => getPlanningDocumentUrl(
  doc && typeof doc === 'object' ? doc : null,
  doc && typeof doc !== 'object' ? doc : undefined
);

const DOCUMENT_PREVIEW_PLACEHOLDER = '/placeholder-document.svg';
const getDocumentPreviewUrl = (doc) => doc?.thumbnail_url || doc?.thumbnail || DOCUMENT_PREVIEW_PLACEHOLDER;
const normalizeDocumentSelectionKey = (value) => String(value || '').trim();

const buildDocumentPreviewCursor = (expanded) => {
  const actionPath = expanded
    ? '<path d="M12 18h12" stroke="#0D0F13" stroke-width="3" stroke-linecap="round"/>'
    : '<path d="M12 18h12M18 12v12" stroke="#0D0F13" stroke-width="3" stroke-linecap="round"/>';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="18" cy="18" r="13" fill="%23B8941F" stroke="%23F5EBD8" stroke-width="2"/><path d="M27.5 27.5L36 36" stroke="%23F5EBD8" stroke-width="4" stroke-linecap="round"/>${actionPath}</svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 18 18, ${expanded ? 'zoom-out' : 'zoom-in'}`;
};

const documentMatchesSelectionKey = (doc, targetKey) => {
  if (!targetKey) return false;
  return [
    getDocumentOpenUrl(doc),
    doc?.thumbnail_url,
    doc?.thumbnail,
    getDocumentPreviewUrl(doc),
  ].some((value) => normalizeDocumentSelectionKey(value) === targetKey);
};

const normalizeAnalyticsReference = (value) => String(value || '').trim().toUpperCase();

const getPlanningApplicationReferenceFromPath = () => {
  if (typeof window === 'undefined') return null;
  const pathname = window.location.pathname || '';
  const match = pathname.match(/^\/(?:planning-application|planning-updates)\/([^/?#]+)\/([^/?#]+)\/([^/?#]+)/i);
  if (!match) return null;
  return decodeURIComponent(`${match[1]}/${match[2]}/${match[3]}`);
};
const WEEKLY_REPORTS_PROMPT_DISMISS_MS = 24 * 60 * 60 * 1000;
const WEEKLY_REPORTS_PROMPT_DISMISS_KEY = 'cityscope_weekly_reports_prompt_dismissed_until';
const WEEKLY_REPORTS_PROMPT_NEVER_KEY = 'cityscope_weekly_reports_prompt_never';

const normalizePlanningArticleFromApplication = (application) => {
  if (!application || typeof application !== 'object') return null;

  const article =
    application.publishedArticle ||
    application.published_article ||
    application.planningArticle ||
    application.planning_article ||
    application.journalArticle ||
    application.journal_article ||
    application.article ||
    null;

  if (!article || typeof article !== 'object') return null;

  const slug = String(article.slug || '').trim();
  const title = String(article.title || '').trim();
  const markdown = String(article.markdown || '').trim();
  if (!slug || !title || !markdown) return null;

  return {
    ...article,
    reference_no: article.reference_no || article.referenceNo || application.reference_no || application.ref,
    title,
    slug,
    excerpt: article.excerpt || '',
    markdown,
    author: article.author || 'City Scope',
    location: article.location || 'Edinburgh',
    publishedAt: article.publishedAt || article.published_at || article.published || null,
    updatedAt: article.updatedAt || article.updated_at || null,
    readingTime: article.readingTime || article.reading_time || 3,
    coverLabel: article.coverLabel || article.cover_label || 'Worth Watching',
    tags: Array.isArray(article.tags) ? article.tags : ['Planning Applications'],
    seo: article.seo || {},
  };
};

const hasPlanningArticleSignal = (application) => {
  if (!application || typeof application !== 'object') return false;
  if (normalizePlanningArticleFromApplication(application)) return true;
  return Boolean(
    application.hasPlanningArticle ||
    application.has_planning_article ||
    application.hasPublishedArticle ||
    application.has_published_article ||
    application.planningArticleSlug ||
    application.planning_article_slug ||
    application.articleSlug ||
    application.article_slug
  );
};

const isPositiveFlag = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const isWorthWatchingApplication = (application) => {
  if (!application || typeof application !== 'object') return false;
  return Boolean(
    isPositiveFlag(application.interesting) ||
    isPositiveFlag(application.worthWatching) ||
    isPositiveFlag(application.worth_watching) ||
    isPositiveFlag(application.isWorthWatching) ||
    isPositiveFlag(application.is_worth_watching)
  );
};

const hasMapCoordinates = (application) => {
  if (!application || typeof application !== 'object') return false;
  const latitude = Number(application.latitude ?? application.lat);
  const longitude = Number(application.longitude ?? application.lng ?? application.lon);
  return Number.isFinite(latitude) && Number.isFinite(longitude);
};

const getJournalPhotoInsertTargets = (sectionCount, photoCount) => {
  if (!sectionCount || !photoCount) return [];
  if (photoCount === 1) return [Math.min(1, sectionCount - 1)];

  return [
    Math.min(1, sectionCount - 1),
    Math.min(Math.max(3, Math.floor(sectionCount * 0.45)), sectionCount - 1),
    Math.min(Math.max(5, Math.floor(sectionCount * 0.72)), sectionCount - 1),
  ].slice(0, photoCount);
};

const renderModalJournalSection = (section, index) => {
  if (section.type === 'heading') {
    return (
      <h3 key={`section-${index}`} className="mt-2 font-['Newsreader'] text-2xl font-medium leading-tight tracking-[-0.01em] text-[var(--c-text-primary)]">
        {renderInlineMarkdown(section.text)}
      </h3>
    );
  }
  if (section.type === 'list' || section.type === 'bullets') {
    return (
      <ul key={`section-${index}`} className="space-y-2.5">
        {section.items.map((item, itemIndex) => (
          <li key={itemIndex} className="flex gap-3 text-[16px] leading-relaxed text-[var(--c-text-secondary)] sm:text-[17px]">
            <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--c-accent)]" />
            <span>{renderInlineMarkdown(item)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (section.type === 'callout') {
    return (
      <div key={`section-${index}`} className="border border-[var(--c-accent)]/30 bg-[rgba(var(--c-accent-rgb),0.06)] px-5 py-4 text-[16px] leading-relaxed text-[var(--c-text-primary)]">
        {renderInlineMarkdown(section.text)}
      </div>
    );
  }
  if (section.type === 'divider') {
    return <hr key={`section-${index}`} className="border-[var(--c-border)]" />;
  }
  return (
    <p key={`section-${index}`} className="text-[16px] leading-[1.78] text-[var(--c-text-secondary)] sm:text-[17px]">
      {renderInlineMarkdown(section.text)}
    </p>
  );
};

const renderModalJournalSectionsWithPhotos = (sections, photos) => {
  const nodes = [];
  const targets = getJournalPhotoInsertTargets(sections.length, photos.length);
  let photoIndex = 0;

  sections.forEach((section, index) => {
    nodes.push(renderModalJournalSection(section, index));

    if (
      photoIndex < photos.length &&
      index >= targets[photoIndex] &&
      section.type !== 'heading' &&
      section.type !== 'divider'
    ) {
      nodes.push(
        <PlanningArticleInlinePhoto
          key={`photo-${photoIndex}`}
          item={photos[photoIndex]}
          priority={photoIndex === 0}
          className="my-6"
        />
      );
      photoIndex += 1;
    }
  });

  while (photoIndex < photos.length) {
    nodes.push(
      <PlanningArticleInlinePhoto
        key={`photo-${photoIndex}`}
        item={photos[photoIndex]}
        priority={photoIndex === 0}
        className="my-6"
      />
    );
    photoIndex += 1;
  }

  return nodes;
};

const PlanningApplicationModal = ({
  isOpen,
  onClose = () => {},
  application: incomingApplication,
  showAiSummaries,
  onImageClick,
  isFavorite = false,
  onToggleFavorite,
  initialTab = 'overview',
  initialDocumentUrl = null,
  initialImageExpanded = false,
  renderInline = false,
  onNavigateApplication,
  onDetailHydrated,
  allApplications: allApplicationsProp,
  publishedArticle = null,
  publishedArticleLookupComplete = false,
}) => {
  const [hasMounted, setHasMounted] = useState(false);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [docDirection, setDocDirection] = useState(1);
  const [loadingDoc, setLoadingDoc] = useState(null);
  const [primingDoc, setPrimingDoc] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'overview'); // 'overview' | 'applications' | 'timeline' | 'reports' | 'documents'
  const [tabDirection, setTabDirection] = useState(1);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [useExpandedDetailLayout, setUseExpandedDetailLayout] = useState(false);
  const initialImageExpandedKeyRef = useRef(null);
  const tabListRef = useRef(null);
  const modalAnalyticsRef = useRef(null);
  const pendingTabTriggerRef = useRef('initial');
  const trackModalCloseRef = useRef(() => {});

  // Integrated Report tab state
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [generatedReport, setGeneratedReport] = useState('');
  const [reportError, setReportError] = useState('');
  const [reportPersistenceNotice, setReportPersistenceNotice] = useState(null);
  const [copiedReport, setCopiedReport] = useState(false);
  const [previousReports, setPreviousReports] = useState([]);
  const [selectedPrevReportId, setSelectedPrevReportId] = useState(null);
  const [showOriginalOverview, setShowOriginalOverview] = useState(false);
  const [showOriginalReport, setShowOriginalReport] = useState(false);

  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formReloadIndex, setFormReloadIndex] = useState(0);
  const formCacheRef = useRef({});
  const { hydratedApplication, isHydratingDetail } = usePlanningApplicationDetailHydration({
    isOpen,
    application: incomingApplication,
    onDetailHydrated,
  });
  const [fetchedArticle, setFetchedArticle] = useState(null);
  const articleCacheRef = useRef({});
  const selectedApplication = hydratedApplication && applicationIdentityKey(hydratedApplication) === applicationIdentityKey(incomingApplication || {})
    ? hydratedApplication : incomingApplication;
  const application = useMemo(() => selectedApplication ? sanitizePublicPlanningRecord(selectedApplication) : selectedApplication,
    [selectedApplication]);
  const sourceRecordOnly = isSourceOnlyPlanningRecord(application);
  const discussionsFeatureEnabled = usePlanningDiscussionsFeatureFlag();
  const discussionsEnabled = discussionsFeatureEnabled && !sourceRecordOnly;
  const [discussionPreviewComments, setDiscussionPreviewComments] = useState([]);
  const [discussionCommentCount, setDiscussionCommentCount] = useState(0);
  const [showWeeklyReportsPrompt, setShowWeeklyReportsPrompt] = useState(false);
  const reportOfferRef = useRef(null);
  const reportOfferTrackedRef = useRef(new Set());
  // Sticky compact-bar visibility based on scroll position of the modal content
  const contentScrollRef = useRef(null);
  const [isContentScrolled, setIsContentScrolled] = useState(false);
  const handleContentScroll = useCallback((event) => {
    const top = event.currentTarget?.scrollTop || 0;
    setIsContentScrolled(top > 56);
  }, []);

  const scrollTabList = useCallback((direction) => {
    const tabList = tabListRef.current;
    if (!tabList) return;

    tabList.scrollBy({
      left: direction * Math.max(160, Math.round(tabList.clientWidth * 0.65)),
      behavior: 'smooth',
    });
  }, []);

  // Keep top-comments preview in sync with any discussion updates
  const safeReferenceForPreview = useMemo(
    () => String(incomingApplication?.reference_no || incomingApplication?.ref || '').replace(/\s+/g, '').trim().toUpperCase(),
    [incomingApplication?.reference_no, incomingApplication?.ref]
  );
  useEffect(() => {
    if (!discussionsEnabled || !safeReferenceForPreview) return;
    const sync = () => {
      setDiscussionPreviewComments(getDiscussionPreviewComments(safeReferenceForPreview));
      setDiscussionCommentCount(getDiscussionCount(safeReferenceForPreview));
    };
    sync();
    return subscribeToDiscussionUpdates(sync);
  }, [discussionsEnabled, safeReferenceForPreview]);

  useEffect(() => {
    setHasMounted(true);
    return () => setHasMounted(false);
  }, []);

  // Get reference number with fallback
  const safeReferenceNo = application?.reference_no || application?.ref || '';
  const applicationCouncilSlug = getCouncilConfig(application?.source_council)?.slug || 'edinburgh';
  const qualifiedReferenceNo = formatCouncilQualifiedReference(safeReferenceNo, applicationCouncilSlug);
  const applicationIdentity = applicationIdentityKey(application || {});
  const trackApplicationDetailAction = useCallback((action, context = {}) => {
    if (!safeReferenceNo) return;
    trackCoreJourneyEvent('planning_research', action, {
      source: 'application_modal',
      placement: context.placement || 'application_detail_modal',
      status: context.status,
      reason: context.reason,
      entityType: 'planning_application',
      entityId: safeReferenceNo,
      metadata: context.metadata || {},
    });
  }, [safeReferenceNo]);
  const initialPlanningArticle = useMemo(
    () => normalizePlanningArticleFromApplication(application),
    [application]
  );
  const shouldRequestPlanningArticle = useMemo(() => {
    if (sourceRecordOnly) return false;
    if (publishedArticleLookupComplete || publishedArticle || initialPlanningArticle) return false;
    return isWorthWatchingApplication(application) || hasPlanningArticleSignal(application);
  }, [application, initialPlanningArticle, publishedArticle, publishedArticleLookupComplete, sourceRecordOnly]);
  const normalizedPublishedArticle = useMemo(() => {
    const article = publishedArticle || initialPlanningArticle || fetchedArticle;
    if (!article) return null;
    return {
      ...article,
      sections: Array.isArray(article.sections)
        ? article.sections
        : parseMarkdownToBlogSections(article.markdown || ''),
    };
  }, [publishedArticle, initialPlanningArticle, fetchedArticle]);
  const hasFormSignal = useMemo(() => !sourceRecordOnly && hasApplicationFormData(application), [application, sourceRecordOnly]);
  const inlineFormData = useMemo(() => sourceRecordOnly ? null : extractInlineApplicationFormData(application), [application, sourceRecordOnly]);
  const showFormTab = !sourceRecordOnly && Boolean(hasFormSignal || inlineFormData || formData);
  const overviewFormPayload = useMemo(
    () => formData || inlineFormData || null,
    [formData, inlineFormData]
  );
  const overviewNormalizedForm = useMemo(
    () => normalizeFormPayload(overviewFormPayload),
    [overviewFormPayload]
  );
  const overviewFormSummary = useMemo(
    () => buildFormDataSummary(overviewNormalizedForm.fields),
    [overviewNormalizedForm.fields]
  );
  const handleImageOpen = useCallback((imageUrl) => {
    if (!imageUrl) return;
    if (typeof onImageClick === 'function') {
      onImageClick(imageUrl);
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }
  }, [onImageClick]);

  // Track view when modal opens
  useEffect(() => {
    if (!isOpen || !application || !safeReferenceNo) return;

    // Use centralized tracking with built-in 60-second cooldown
    trackViewWithCooldown(safeReferenceNo, {
      source: detectSource(),
      council: applicationCouncilSlug,
      category: application.category || application.primaryCategory,
      address: application.address || application.location,
      scale_rating: application.scale_rating
    });
  }, [application, applicationCouncilSlug, isOpen, safeReferenceNo]);

  useEffect(() => {
    if (!isOpen || !safeReferenceNo || !shouldRequestPlanningArticle) {
      setFetchedArticle(null);
      return;
    }

    const cacheKey = `${applicationCouncilSlug}:${String(safeReferenceNo).replace(/\s+/g, '').trim().toUpperCase()}`;
    const cachedEntry = articleCacheRef.current[cacheKey];
    if (cachedEntry && (Date.now() - cachedEntry.fetchedAt) < ARTICLE_CACHE_TTL_MS) {
      setFetchedArticle(cachedEntry.data || null);
      return;
    }

    let cancelled = false;
    setFetchedArticle(null);
    fetchPlanningArticleByReference(safeReferenceNo, { council: applicationCouncilSlug })
      .then((article) => {
        if (cancelled) return;
        articleCacheRef.current[cacheKey] = {
          fetchedAt: Date.now(),
          data: article || null,
        };
        setFetchedArticle(article || null);
      })
      .catch(() => {
        if (cancelled) return;
        articleCacheRef.current[cacheKey] = {
          fetchedAt: Date.now(),
          data: null,
        };
        setFetchedArticle(null);
      });

    return () => {
      cancelled = true;
    };
  }, [applicationCouncilSlug, isOpen, safeReferenceNo, shouldRequestPlanningArticle]);

  useEffect(() => {
    if (isOpen && initialTab === 'journal' && normalizedPublishedArticle) {
      pendingTabTriggerRef.current = 'initial_journal_tab';
      setActiveTab('journal');
    }
  }, [isOpen, initialTab, normalizedPublishedArticle]);

  const dismissWeeklyReportsPrompt = useCallback((mode = 'later') => {
    setShowWeeklyReportsPrompt(false);
    if (typeof window === 'undefined') return;

    try {
      if (mode === 'never') {
        window.localStorage.setItem(WEEKLY_REPORTS_PROMPT_NEVER_KEY, '1');
        return;
      }

      window.localStorage.setItem(
        WEEKLY_REPORTS_PROMPT_DISMISS_KEY,
        String(Date.now() + WEEKLY_REPORTS_PROMPT_DISMISS_MS)
      );
    } catch {
      // Storage can be unavailable in private browsing or locked-down embeds.
    }
  }, []);

  useEffect(() => {
    const highIntentTab = ['documents', 'form', 'reports', 'discussion'].includes(activeTab);
    if (sourceRecordOnly || !isOpen || !safeReferenceNo || !highIntentTab || typeof window === 'undefined') {
      setShowWeeklyReportsPrompt(false);
      return undefined;
    }

    setShowWeeklyReportsPrompt(false);

    try {
      if (window.localStorage.getItem(WEEKLY_REPORTS_PROMPT_NEVER_KEY) === '1') {
        return undefined;
      }

      const dismissedUntil = Number(window.localStorage.getItem(WEEKLY_REPORTS_PROMPT_DISMISS_KEY) || 0);
      if (Number.isFinite(dismissedUntil) && dismissedUntil > Date.now()) {
        return undefined;
      }
    } catch {
      // If storage is unavailable, still allow the prompt for this session.
    }

    setShowWeeklyReportsPrompt(true);
    return undefined;
  }, [activeTab, isOpen, safeReferenceNo, sourceRecordOnly]);

  useEffect(() => {
    if (!showWeeklyReportsPrompt || !isOpen || !safeReferenceNo || typeof window === 'undefined') {
      return undefined;
    }

    const offer = reportOfferRef.current;
    if (!offer) return undefined;
    const offerKey = `${safeReferenceNo}:${activeTab}`;
    if (reportOfferTrackedRef.current.has(offerKey)) return undefined;

    const recordImpression = () => {
      if (reportOfferTrackedRef.current.has(offerKey)) return;
      reportOfferTrackedRef.current.add(offerKey);
      trackCoreJourneyEvent('reports', 'report_offer_viewed', {
        source: 'application_detail_context',
        placement: `application_${activeTab}_panel`,
        entityType: 'planning_application',
        entityId: safeReferenceNo,
        status: 'visible',
      });
    };

    if (!('IntersectionObserver' in window)) {
      const bounds = offer.getBoundingClientRect();
      if (bounds.bottom > 0 && bounds.right > 0 && bounds.top < window.innerHeight && bounds.left < window.innerWidth) {
        recordImpression();
      }
      return undefined;
    }

    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) {
        recordImpression();
        observer.disconnect();
      }
    }, { threshold: 0.01 });
    observer.observe(offer);
    return () => observer.disconnect();
  }, [activeTab, isOpen, safeReferenceNo, showWeeklyReportsPrompt]);

  // Handle close
  const handleClose = useCallback((closeReason = 'close_button') => {
    trackModalCloseRef.current(closeReason);
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  }, [onClose]);

  // Handle browser back button
  useEffect(() => {
    if (renderInline) return;
    const handlePopState = () => {
      if (isOpen) {
        handleClose('browser_back');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, handleClose, renderInline]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (isImageExpanded) {
          e.preventDefault();
          setIsImageExpanded(false);
          return;
        }
        handleClose('escape_key');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isImageExpanded, isOpen, handleClose]);

  // Access global applications and modal opener to build related list and switch between apps
  const planningContext = usePlanningContext?.() || {};
  const contextApplications = Array.isArray(planningContext?.applications)
    ? planningContext.applications
    : EMPTY_ARRAY;
  const allApplications = Array.isArray(allApplicationsProp)
    ? allApplicationsProp
    : contextApplications;
  const contextOpenApplicationModal = typeof planningContext?.openApplicationModal === 'function'
    ? planningContext.openApplicationModal
    : undefined;
  const navigateToApplication = useCallback((nextApplication, options = {}) => {
    if (!nextApplication) return;
    if (typeof onNavigateApplication === 'function') {
      onNavigateApplication(nextApplication, options);
      return;
    }
    if (contextOpenApplicationModal) {
      contextOpenApplicationModal(nextApplication, options);
    }
  }, [onNavigateApplication, contextOpenApplicationModal]);
  const relatedApplications = useMemo(() => {
    if (!application || !Array.isArray(allApplications) || allApplications.length === 0) return [];

    let list = [];

    // Prefer backend grouping when available
    if (application.group_id) {
      list = allApplications.filter(a => a.group_id && a.group_id === application.group_id);
    }

    // Fallback to address-based normalization when group_id is missing
    if (list.length === 0) {
      const addr = application.address || application.location;
      if (!addr) return [];
      const normalized = (str) => (str || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const base = normalized(addr)
        .replace(/^(flat|apartment|apt|unit)\s+\d+[a-z]*,?\s*/i, '')
        .replace(/^\d+[a-z]*[f/-]\d+[a-z]*,?\s*/i, '')
        .replace(/^pf\d+\s*,?\s*/i, '');
      const isSame = (a) => {
        const aAddr = a.address || a.location;
        if (!aAddr) return false;
        const b = normalized(aAddr)
          .replace(/^(flat|apartment|apt|unit)\s+\d+[a-z]*,?\s*/i, '')
          .replace(/^\d+[a-z]*[f/-]\d+[a-z]*,?\s*/i, '')
          .replace(/^pf\d+\s*,?\s*/i, '');
        return b === base;
      };
      list = allApplications.filter(isSame);
    }

    // Deduplicate by reference number
    const seen = new Set();
    list = list.filter(app => {
      const ref = app.reference_no || app.ref;
      if (!ref) return true;
      if (seen.has(ref)) return false;
      seen.add(ref);
      return true;
    });

    // Sort newest first by received_date then scraped_date
    list.sort((a,b) => new Date(b.received_date || b.scraped_date || 0).getTime() - new Date(a.received_date || a.scraped_date || 0).getTime());
    return list;
  }, [allApplications, application]);

  // Index of the current application within the related set
  const currentRelatedIndex = useMemo(() => {
    if (!application || !relatedApplications.length) return -1;
    const ref = application.reference_no || application.ref;
    return relatedApplications.findIndex(a => (a.reference_no || a.ref) === ref);
  }, [relatedApplications, application]);

  const goToRelated = useCallback((direction) => {
    if (!navigateToApplication || !relatedApplications.length || currentRelatedIndex < 0) return;
    const nextIndex = (currentRelatedIndex + direction + relatedApplications.length) % relatedApplications.length;
    const nextApp = relatedApplications[nextIndex];
    if (nextApp) {
      navigateToApplication(nextApp);
    }
  }, [navigateToApplication, relatedApplications, currentRelatedIndex]);

  // Keyboard navigation for related application paginator
  useEffect(() => {
    if (!isOpen) return;
    if (!relatedApplications || relatedApplications.length <= 1) return;
    const handleArrows = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToRelated(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToRelated(1);
      }
    };
    document.addEventListener('keydown', handleArrows);
    return () => document.removeEventListener('keydown', handleArrows);
  }, [isOpen, relatedApplications, currentRelatedIndex, goToRelated]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (renderInline) return;
    if (e.target === e.currentTarget) {
      handleClose('backdrop_click');
    }
  };

  // Note: Do not return early before hooks; guard rendering later

  // Get application details with fallbacks (guard when application is null)
  const description = application?.description || application?.proposal || application?.details || '';
  const address = application?.address || application?.location || 'Unknown Address';
  const displayDescription = (showAiSummaries && application?.ai_summary) || description || "No description available.";
  const isWorthWatching = isWorthWatchingApplication(application);

  // Get documents with stable reference when missing
  const documents = useMemo(() => {
    return application?.documents ? application.documents : EMPTY_ARRAY;
  }, [application]);
  const documentListNotice = sourceRecordOnly ? londonDocumentListNotice(application?.document_index) : null;
  const reportedDecisionNotice = sourceRecordOnly ? londonReportedDecisionNotice(application?.reported_source_facts) : null;
  const sourceRefreshNotice = sourceRecordOnly ? londonSourceRefreshNotice(application?.source_refresh) : null;
  const [previewDocs, setPreviewDocs] = useState(documents);
  useEffect(() => {
    // Skip if identical by reference
    if (previewDocs === documents) return;
    // Shallow equality guard for common doc shapes
    const sameLength = (previewDocs?.length || 0) === (documents?.length || 0);
    const shallowSame = sameLength && previewDocs.every((d, i) => {
      const nd = documents[i];
      return (
        (getDocumentOpenUrl(d) || d) === (getDocumentOpenUrl(nd) || nd) &&
        (d?.thumbnail_url || d?.thumbnail || '') === (nd?.thumbnail_url || nd?.thumbnail || '')
      );
    });
    if (shallowSame) return;
    setPreviewDocs(documents);
    setCurrentDocIndex(0);
  }, [documents, previewDocs]);
  useEffect(() => {
    if (!isOpen || !initialDocumentUrl || previewDocs.length === 0) return;

    const targetKey = normalizeDocumentSelectionKey(initialDocumentUrl);
    const nextIndex = previewDocs.findIndex((doc) => documentMatchesSelectionKey(doc, targetKey));
    if (nextIndex < 0 || nextIndex === currentDocIndex) return;

    setDocDirection(nextIndex > currentDocIndex ? 1 : -1);
    setCurrentDocIndex(nextIndex);
  }, [currentDocIndex, initialDocumentUrl, isOpen, previewDocs]);
  const currentDoc = previewDocs.length > 0 ? previewDocs[currentDocIndex] : null;
  const currentDocUrl = getDocumentOpenUrl(currentDoc);
  const currentDocPreviewUrl = getDocumentPreviewUrl(currentDoc);
  const mapSnapshotUrl = useMemo(() => getMapSnapshotUrl(application), [application]);
  const isMapSnapshot = !currentDoc && Boolean(mapSnapshotUrl);
  const currentMediaPreviewUrl = currentDoc
    ? currentDocPreviewUrl
    : (mapSnapshotUrl || DOCUMENT_PREVIEW_PLACEHOLDER);
  const currentDocLoadingKey = currentDoc?.thumbnail_url || currentDoc?.thumbnail || currentDocUrl;
  const currentDocPrimingKey = currentDocUrl ? `prime:${currentDocUrl}` : null;
  const currentDocCanPrime = Boolean(
    currentDocUrl &&
    isIdoxDocumentUrl(currentDocUrl) &&
    (application?.link || application?.portal_url)
  );
  const documentPreviewTooltipText = isImageExpanded ? 'Click to collapse' : 'Click to expand';
  const documentPreviewTooltipId = `planning-doc-preview-tooltip-${String(applicationIdentity || safeReferenceNo || 'app').replace(/[^a-z0-9_-]/gi, '-')}`;

  // Get category info
  const categoryInfo = (() => {
    if (sourceRecordOnly && !application?.category) return null;
    if (!application) {
      return getCategoryById('legal_admin');
    }
    try {
      if (application.category) {
        const catData = getCategoryById(application.category);
        if (catData && catData.id && catData.label && catData.icon) {
          return catData;
        }
      }

      const tempApp = {
        category: application.category,
        description: displayDescription,
        reference_no: safeReferenceNo
      };
      const { primaryCategory } = apiOnlyCategorizeApplication(tempApp);
      if (primaryCategory) {
        const catData = getCategoryById(primaryCategory);
        if (catData && catData.id && catData.label && catData.icon) {
          return catData;
        }
      }

      return getCategoryById('legal_admin');
    } catch (error) {
      return getCategoryById('legal_admin');
    }
  })();

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return formatLongPlanningDate(dateStr) || dateStr;
  };

  // Get status styling
  const getStatusStyling = (status) => {
    if (status?.includes("Approved")) {
      return {
        bg: "bg-emerald-500/20",
        border: "border-emerald-500/40",
        text: "text-emerald-400",
        dotColor: "var(--c-success)",
        icon: CheckCircle
      };
    }
    if (status?.includes("Rejected") || status?.includes("Refused")) {
      return {
        bg: "bg-red-500/20",
        border: "border-red-500/40",
        text: "text-red-400",
        dotColor: "var(--c-error)",
        icon: XCircle
      };
    }
    if (status?.includes("Awaiting")) {
      return {
        bg: "bg-amber-500/20",
        border: "border-amber-500/40",
        text: "text-amber-400",
        dotColor: "var(--c-warning)",
        icon: Clock
      };
    }
    return {
      bg: "bg-[var(--c-accent)]/20",
      border: "border-[var(--c-accent)]/40",
      text: "text-[var(--c-accent)]",
      dotColor: "var(--c-accent)",
      icon: AlertCircle
    };
  };

  const statusStyle = getStatusStyling((application && application.status) || '');
  const StatusIcon = statusStyle.icon;

  // Aggregate documents by application when there are multiple related applications
  const documentsByApp = useMemo(() => {
    const sourceApps = (relatedApplications && relatedApplications.length > 1)
      ? relatedApplications
      : (application ? [application] : []);
    const groups = sourceApps.map((app) => {
      const docs = Array.isArray(app?.documents) ? app.documents : [];
      return { app, docs };
    }).filter((g) => g.docs.length > 0);
    return groups;
  }, [relatedApplications, application]);

  const totalDocumentsCount = useMemo(() => {
    return documentsByApp.reduce((acc, g) => acc + (g.docs?.length || 0), 0);
  }, [documentsByApp]);

  const hasPlanningSignals = useMemo(
    () => !sourceRecordOnly && getDisplayPlanningSignals(application, { category: categoryInfo, surface: 'modal' }).length > 0,
    [application, categoryInfo?.id, categoryInfo?.label, sourceRecordOnly]
  );
  const showMapTab = useMemo(() => hasMapCoordinates(application), [application]);

  const modalTabs = useMemo(() => ([
    { id: 'overview', label: 'Overview', shortLabel: 'Overview', icon: <TrendingUp className="w-3.5 h-3.5" /> },
    ...(showMapTab ? [
      { id: 'map', label: 'Map', shortLabel: 'Map', icon: <MapPinned className="w-3.5 h-3.5" /> }
    ] : []),
    ...(hasPlanningSignals ? [
      { id: 'signals', label: 'Tags', shortLabel: 'Tags', icon: <Tags className="w-3.5 h-3.5" /> }
    ] : []),
    ...(showFormTab ? [
      { id: 'form', label: 'Application Insights', shortLabel: 'Insights', icon: <FileSearch className="w-3.5 h-3.5" /> }
    ] : []),
    ...(relatedApplications.length > 1 ? [
      { id: 'applications', label: `Apps (${relatedApplications.length})`, shortLabel: `Apps (${relatedApplications.length})`, icon: <Building className="w-3.5 h-3.5" /> },
      { id: 'timeline', label: 'Timeline', shortLabel: 'Timeline', icon: <Clock className="w-3.5 h-3.5" /> }
    ] : []),
    ...(discussionsEnabled ? [
      { id: 'discussion', label: 'Discussion', shortLabel: 'Discuss', icon: <MessageSquareText className="w-3.5 h-3.5" /> }
    ] : []),
    ...(normalizedPublishedArticle ? [
      { id: 'journal', label: 'Journal', shortLabel: 'Journal', icon: <FileText className="w-3.5 h-3.5" /> }
    ] : []),
    ...(!sourceRecordOnly ? [
      { id: 'reports', label: 'Reports', shortLabel: 'Reports', icon: <FileText className="w-3.5 h-3.5" /> },
    ] : []),
    ...(totalDocumentsCount > 0 || sourceRecordOnly ? [
      { id: 'documents', label: totalDocumentsCount>0 || application?.document_index?.status==='verified_empty' ? `Docs (${totalDocumentsCount})` : 'Docs', shortLabel: totalDocumentsCount>0 || application?.document_index?.status==='verified_empty' ? `Docs (${totalDocumentsCount})` : 'Docs', icon: <FileText className="w-3.5 h-3.5" /> }
    ] : [])
  ]), [discussionsEnabled, hasPlanningSignals, showFormTab, showMapTab, relatedApplications.length, totalDocumentsCount, normalizedPublishedArticle, sourceRecordOnly, application?.document_index?.status]);

  const useFullTabLabels = modalTabs.length <= 4;
  const activeTabIndex = useMemo(
    () => modalTabs.findIndex((tab) => tab.id === activeTab),
    [modalTabs, activeTab]
  );

  const createCurrentModalAnalyticsSession = useCallback((activeTabId) => {
    const availableTabs = modalTabs.map((tab) => ({ id: tab.id, label: tab.label }));
    return createPlanningApplicationModalAnalyticsSession({
      reference: safeReferenceNo,
      activeTab: activeTabId,
      availableTabs,
      baseMetadata: {
        application_status: application?.status,
        application_category: application?.category || application?.primaryCategory || categoryInfo?.id,
        application_category_label: categoryInfo?.label,
        initial_tab: initialTab || 'overview',
        documents_count: totalDocumentsCount,
        related_applications_count: relatedApplications.length,
        has_map: showMapTab,
        has_planning_signals: hasPlanningSignals,
        has_form_data: showFormTab,
        has_discussion: discussionsEnabled,
        has_journal: Boolean(normalizedPublishedArticle),
        render_inline: renderInline,
      },
    });
  }, [
    modalTabs,
    safeReferenceNo,
    application?.status,
    application?.category,
    application?.primaryCategory,
    categoryInfo?.id,
    categoryInfo?.label,
    initialTab,
    totalDocumentsCount,
    relatedApplications.length,
    showMapTab,
    hasPlanningSignals,
    showFormTab,
    discussionsEnabled,
    normalizedPublishedArticle,
    renderInline,
  ]);

  const flushModalTabDwell = useCallback((session, trigger = 'tab_change') => {
    if (!session || session.closeTracked || !session.activeTab || !session.tabEnteredAtMs) return;
    const nowMs = Date.now();
    const durationMs = Math.max(0, nowMs - session.tabEnteredAtMs);
    trackPlanningApplicationModalEvent(session, PLANNING_APPLICATION_MODAL_ACTIONS.tabDwell, {
      tabId: session.activeTab,
      trigger,
      durationMs,
    });
    session.tabEnteredAtMs = nowMs;
  }, []);

  const trackModalClose = useCallback((closeReason = 'programmatic_close') => {
    const session = modalAnalyticsRef.current;
    if (!session || session.closeTracked) return;

    flushModalTabDwell(session, closeReason);
    const durationMs = Math.max(0, Date.now() - session.openedAtMs);
    const abandonedReasons = new Set(['pagehide', 'beforeunload']);
    const closeAction = abandonedReasons.has(closeReason)
      ? PLANNING_APPLICATION_MODAL_ACTIONS.abandoned
      : PLANNING_APPLICATION_MODAL_ACTIONS.closed;

    session.closeTracked = true;
    trackPlanningApplicationModalEvent(session, closeAction, {
      tabId: session.activeTab,
      closeReason,
      durationMs,
      metadata: {
        final_tab: session.activeTab,
      },
    });
    modalAnalyticsRef.current = null;
  }, [flushModalTabDwell]);

  useEffect(() => {
    trackModalCloseRef.current = trackModalClose;
  }, [trackModalClose]);

  const trackModalTabSelected = useCallback((nextTabId, trigger = 'tab_click') => {
    const session = modalAnalyticsRef.current;
    if (!session || session.closeTracked || !nextTabId || nextTabId === activeTab) return;

    session.selectedTabCount += 1;
    trackPlanningApplicationModalEvent(session, PLANNING_APPLICATION_MODAL_ACTIONS.tabSelected, {
      tabId: nextTabId,
      previousTabId: activeTab,
      trigger,
    });
  }, [activeTab]);

  useEffect(() => {
    if (!modalTabs.some((tab) => tab.id === activeTab)) {
      pendingTabTriggerRef.current = 'tab_unavailable';
      setTabDirection(-1);
      setActiveTab('overview');
    }
  }, [activeTab, modalTabs]);

  useEffect(() => {
    if (!isOpen) {
      setIsImageExpanded(false);
      setUseExpandedDetailLayout(false);
      initialImageExpandedKeyRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    setIsImageExpanded(false);
    setUseExpandedDetailLayout(false);
    initialImageExpandedKeyRef.current = null;
  }, [applicationIdentity]);

  useEffect(() => {
    if (!isImageExpanded) {
      setUseExpandedDetailLayout(false);
      return undefined;
    }

    const layoutTimer = window.setTimeout(() => {
      setUseExpandedDetailLayout(true);
    }, DETAIL_COMPANION_EXIT_MS);

    return () => window.clearTimeout(layoutTimer);
  }, [isImageExpanded]);

  useEffect(() => {
    if (!isOpen || !initialImageExpanded || !currentDoc) return;

    const targetKey = [
      applicationIdentity || safeReferenceNo || 'application',
      normalizeDocumentSelectionKey(initialDocumentUrl || currentDocPreviewUrl || currentDocUrl),
    ].join(':');
    if (initialImageExpandedKeyRef.current === targetKey) return;

    initialImageExpandedKeyRef.current = targetKey;
    setIsImageExpanded(true);
  }, [
    applicationIdentity,
    currentDoc,
    currentDocPreviewUrl,
    currentDocUrl,
    initialDocumentUrl,
    initialImageExpanded,
    isOpen,
    safeReferenceNo,
  ]);

  const cycleModalTab = useCallback((direction) => {
    if (!modalTabs.length) return;
    const currentIndex = activeTabIndex >= 0 ? activeTabIndex : 0;
    const nextIndex = (currentIndex + direction + modalTabs.length) % modalTabs.length;
    const nextTab = modalTabs[nextIndex];
    if (nextTab) {
      pendingTabTriggerRef.current = 'keyboard_cycle';
      trackModalTabSelected(nextTab.id, 'keyboard_cycle');
      setTabDirection(direction >= 0 ? 1 : -1);
      setActiveTab(nextTab.id);
    }
  }, [modalTabs, activeTabIndex, trackModalTabSelected]);

  const setActiveTabWithDirection = useCallback((nextTabId, trigger = 'tab_click') => {
    if (!nextTabId || nextTabId === activeTab) return;
    if (nextTabId === 'discussion') {
      trackApplicationDetailAction('discussion_tab_opened', {
        placement: 'modal_tab',
        metadata: { trigger },
      });
    } else if (nextTabId === 'reports') {
      trackApplicationDetailAction('reports_tab_opened', {
        placement: 'modal_tab',
        metadata: { trigger },
      });
    } else if (nextTabId === 'documents') {
      trackApplicationDetailAction('documents_tab_opened', {
        placement: 'modal_tab',
        metadata: { trigger },
      });
    } else if (nextTabId === 'form') {
      trackApplicationDetailAction('application_form_opened', {
        placement: 'modal_tab',
        metadata: { trigger },
      });
    }
    pendingTabTriggerRef.current = trigger;
    trackModalTabSelected(nextTabId, trigger);
    const nextIndex = modalTabs.findIndex((tab) => tab.id === nextTabId);
    if (nextIndex >= 0) {
      setTabDirection(nextIndex > (activeTabIndex >= 0 ? activeTabIndex : 0) ? 1 : -1);
    } else {
      setTabDirection(1);
    }
    setActiveTab(nextTabId);
  }, [activeTab, modalTabs, activeTabIndex, trackApplicationDetailAction, trackModalTabSelected]);

  const expandedOverviewFlags = (() => {
    if (!application) return [];
    const flags = [];
    const addFlag = (flag) => {
      if (!flag?.label || !flag?.value) return;
      flags.push(flag);
    };

    addFlag({
      id: 'status',
      label: 'Status',
      value: application.status || 'Unknown',
      icon: StatusIcon,
      sectionId: 'dates',
    });

    const primaryDate = application.received_date || application.validated_date || application.scraped_date;
    if (primaryDate) {
      addFlag({
        id: 'received',
        label: application.received_date ? 'Received' : 'Updated',
        value: formatDate(primaryDate),
        icon: Calendar,
        sectionId: 'dates',
      });
    }

    if (categoryInfo?.label) {
      addFlag({
        id: 'category',
        label: 'Category',
        value: categoryInfo.label,
        icon: Tags,
        sectionId: 'signals',
      });
    }

    if (application.scale_rating) {
      addFlag({
        id: 'impact',
        label: 'Impact',
        value: `Scale ${application.scale_rating}/10`,
        icon: TrendingUp,
        sectionId: 'impact',
      });
    }

    if (showFormTab) {
      addFlag({
        id: 'insights',
        label: 'Insights',
        value: overviewFormSummary.totalFields > 0
          ? `${overviewFormSummary.filledFields}/${overviewFormSummary.totalFields} fields`
          : 'Form data',
        icon: FileSearch,
        tabId: 'form',
      });
    }

    if (hasPlanningSignals) {
      addFlag({
        id: 'tags',
        label: 'Tags',
        value: 'Planning signals',
        icon: Flag,
        tabId: 'signals',
      });
    }

    if (totalDocumentsCount > 0) {
      addFlag({
        id: 'docs',
        label: 'Docs',
        value: `${totalDocumentsCount} file${totalDocumentsCount === 1 ? '' : 's'}`,
        icon: FileText,
        tabId: 'documents',
      });
    }

    if (discussionsEnabled) {
      addFlag({
        id: 'discussion',
        label: 'Discussion',
        value: discussionCommentCount > 0 ? `${discussionCommentCount} comment${discussionCommentCount === 1 ? '' : 's'}` : 'No comments',
        icon: MessageSquareText,
        tabId: 'discussion',
      });
    }

    return flags.slice(0, 6);
  })();
  const showExpandedOverviewHeadlines = isImageExpanded && activeTab === 'overview' && expandedOverviewFlags.length > 0;

  const handleExpandedOverviewFlagClick = useCallback((flag) => {
    if (!flag) return;
    trackApplicationDetailAction('expanded_overview_flag_clicked', {
      placement: 'expanded_document_flags',
      metadata: {
        flag_id: flag.id,
        target_section: flag.sectionId || null,
        target_tab: flag.tabId || 'overview',
      },
    });

    setIsImageExpanded(false);

    const targetTab = flag.tabId || 'overview';
    if (targetTab !== activeTab) {
      setActiveTabWithDirection(targetTab, 'expanded_document_flag_click');
    }

    if (flag.sectionId && typeof window !== 'undefined') {
      window.setTimeout(() => {
        const section = document.querySelector(`[data-overview-section="${flag.sectionId}"]`);
        section?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, DETAIL_COMPANION_EXIT_MS + 80);
    }
  }, [activeTab, setActiveTabWithDirection, trackApplicationDetailAction]);

  useEffect(() => {
    if (!isOpen || !application || !safeReferenceNo || !modalTabs.length) return;

    const resolvedTab = modalTabs.some((tab) => tab.id === activeTab)
      ? activeTab
      : modalTabs[0]?.id || 'overview';
    let session = modalAnalyticsRef.current;

    if (session && session.reference !== safeReferenceNo) {
      trackModalClose('application_switched');
      session = null;
    }

    if (!session || session.closeTracked) {
      const nextSession = createCurrentModalAnalyticsSession(resolvedTab);
      modalAnalyticsRef.current = nextSession;
      trackPlanningApplicationModalEvent(nextSession, PLANNING_APPLICATION_MODAL_ACTIONS.opened, {
        tabId: resolvedTab,
        trigger: pendingTabTriggerRef.current || 'initial',
        metadata: {
          opened_at: new Date().toISOString(),
        },
      });
      const initialSessionId = nextSession.sessionId;
      window.setTimeout(() => {
        const currentSession = modalAnalyticsRef.current;
        if (!currentSession || currentSession.sessionId !== initialSessionId || currentSession.closeTracked) return;
        trackPlanningApplicationModalEvent(currentSession, PLANNING_APPLICATION_MODAL_ACTIONS.tabViewed, {
          tabId: resolvedTab,
          trigger: 'initial',
          metadata: {
            tab_view_index: 1,
          },
        });
      }, 75);
      pendingTabTriggerRef.current = 'programmatic';
      return;
    }

    if (session.activeTab !== resolvedTab) {
      const previousTabId = session.activeTab;
      const trigger = pendingTabTriggerRef.current || 'programmatic';
      flushModalTabDwell(session, `before_${trigger}`);
      session.activeTab = resolvedTab;
      session.tabEnteredAtMs = Date.now();
      session.viewedTabs.add(resolvedTab);
      trackPlanningApplicationModalEvent(session, PLANNING_APPLICATION_MODAL_ACTIONS.tabViewed, {
        tabId: resolvedTab,
        previousTabId,
        trigger,
        metadata: {
          tab_view_index: session.viewedTabs.size,
        },
      });
      pendingTabTriggerRef.current = 'programmatic';
    }
  }, [
    isOpen,
    application,
    safeReferenceNo,
    activeTab,
    modalTabs,
    createCurrentModalAnalyticsSession,
    flushModalTabDwell,
    trackModalClose,
  ]);

  useEffect(() => {
    if (!isOpen) {
      trackModalClose('programmatic_close');
    }
  }, [isOpen, trackModalClose]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const trackAbandon = (reason) => {
      trackModalCloseRef.current(reason);
    };
    const handlePageHide = () => trackAbandon('pagehide');
    const handleBeforeUnload = () => trackAbandon('beforeunload');

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const activeSession = modalAnalyticsRef.current;
      const nextReference = getPlanningApplicationReferenceFromPath();
      const closeReason = activeSession && nextReference && normalizeAnalyticsReference(nextReference) !== normalizeAnalyticsReference(activeSession.reference)
        ? 'application_switched'
        : 'unmount';
      trackModalCloseRef.current(closeReason);
    };
  }, []);

  useEffect(() => {
    const tabList = tabListRef.current;
    if (!tabList) return;

    const activeButton = tabList.querySelector('[aria-selected="true"]');
    activeButton?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth',
    });
  }, [activeTab]);

  const tabPanelVariants = {
    enter: (direction) => ({
      opacity: 0,
      x: direction >= 0 ? 22 : -22,
      y: 4,
      filter: 'blur(2px)',
    }),
    center: {
      opacity: 1,
      x: 0,
      y: 0,
      filter: 'blur(0px)',
      transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
    },
    exit: (direction) => ({
      opacity: 0,
      x: direction >= 0 ? -18 : 18,
      y: -2,
      filter: 'blur(2px)',
      transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    }),
  };

  const detailCompanionVariants = {
    enter: {
      opacity: 0,
      x: 24,
      y: 8,
      scale: 0.985,
      filter: 'blur(4px)',
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        opacity: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
        x: { type: 'spring', stiffness: 260, damping: 28, mass: 0.8 },
        y: { type: 'spring', stiffness: 260, damping: 28, mass: 0.8 },
        scale: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
        filter: { duration: 0.18, ease: 'easeOut' },
      },
    },
    exit: {
      opacity: 0,
      x: 30,
      y: 6,
      scale: 0.985,
      filter: 'blur(5px)',
      transition: {
        opacity: { duration: 0.26, ease: [0.4, 0, 1, 1] },
        x: { duration: 0.28, ease: [0.4, 0, 1, 1] },
        y: { duration: 0.28, ease: [0.4, 0, 1, 1] },
        scale: { duration: 0.24, ease: [0.4, 0, 1, 1] },
        filter: { duration: 0.24, ease: 'easeIn' },
      },
    },
  };

  const docPageVariants = {
    enter: (direction) => ({
      opacity: 0,
      x: direction >= 0 ? 26 : -26,
      scale: 0.992,
    }),
    center: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
    },
    exit: (direction) => ({
      opacity: 0,
      x: direction >= 0 ? -22 : 22,
      scale: 0.992,
      transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    }),
  };

  const overviewCardStagger = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.08, delayChildren: 0.04 },
    },
  };

  const overviewCardItem = {
    hidden: { opacity: 0, y: 10, scale: 0.996 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: 'spring', stiffness: 260, damping: 24, mass: 0.85 },
    },
  };

  // URL for sharing this modal directly (deterministic for SSR/CSR parity)
  const modalShareUrl = useMemo(() => {
    return buildPlanningApplicationUrl(safeReferenceNo, applicationCouncilSlug);
  }, [applicationCouncilSlug, safeReferenceNo]);
  const reportSignupPromptUrl = useMemo(() => buildCityScopeUtmUrl('/reports/signup', {
    ...CITYSCOPE_INTERNAL_CTA_ATTRIBUTION,
    src: 'application_detail_prompt',
    utmCampaign: 'planning_reports',
    utmContent: 'application_detail_prompt',
  }), []);

  // Data for ShareButton
  const shareApplication = {
    reference: safeReferenceNo,
    description,
    aiSummary: application?.ai_summary,
    location: address,
    status: (application && application.status) || 'Unknown',
    decision_date: application?.decision_date,
    application_type: application?.application_type,
  };
  const shareDocuments = (documents || []).map(doc => ({
    thumbnail_url: doc.thumbnail_url || doc.thumbnail || getDocumentOpenUrl(doc),
    document_url: getDocumentOpenUrl(doc),
  }));

  // Load and save reports (shared storage with legacy modal)
  const loadPreviousReports = useCallback(() => {
    try {
      const allReports = JSON.parse(localStorage.getItem('planning_reports') || '[]');
      const appRef = application.reference_no || application.ref;
      const appReports = allReports.filter(r => r.reference_no === appRef);
      setPreviousReports(appReports);
    } catch {
      setPreviousReports([]);
    }
  }, [application]);

  useEffect(() => {
    if (isOpen) {
      setGeneratedReport('');
      setReportError('');
      setReportPersistenceNotice(null);
      setCopiedReport(false);
      setTabDirection(1);
      pendingTabTriggerRef.current = 'modal_open';
      setActiveTab(initialTab || 'overview');
      loadPreviousReports();
    }
  }, [isOpen, initialTab, loadPreviousReports]);

  useEffect(() => {
    if (!showFormTab && activeTab === 'form') {
      pendingTabTriggerRef.current = 'form_tab_unavailable';
      setTabDirection(-1);
      setActiveTab('overview');
    }
  }, [showFormTab, activeTab]);

  useEffect(() => {
    if (!isOpen || !application || activeTab !== 'form') return;
    setFormError('');

    const ref = safeReferenceNo;
    if (!ref) {
      setFormData(inlineFormData || null);
      setFormLoading(false);
      return;
    }

    if (!hasFormSignal) {
      setFormData(inlineFormData || null);
      setFormLoading(false);
      return;
    }

    if (inlineFormData) {
      setFormData((prev) => prev || inlineFormData);
    }

    const formCacheKey = applicationIdentity || `${applicationCouncilSlug}:${ref}`;
    const cachedEntry = formCacheRef.current[formCacheKey];
    if (
      cachedEntry &&
      cachedEntry.data &&
      (Date.now() - cachedEntry.fetchedAt) < FORM_CACHE_TTL_MS
    ) {
      setFormData(cachedEntry.data);
      setFormLoading(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    setFormLoading(!inlineFormData);

    fetch(`/api/application-form?ref=${encodeURIComponent(ref)}&council=${encodeURIComponent(applicationCouncilSlug)}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 404) return null;
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        if (!payload) {
          setFormData(inlineFormData || null);
          return;
        }
        const data = payload?.data || payload;
        formCacheRef.current[formCacheKey] = {
          data,
          fetchedAt: Date.now(),
        };
        setFormData(data);
      })
      .catch((err) => {
        if (!active || err?.name === 'AbortError') return;
        setFormError('Unable to load application form data.');
        setFormData(inlineFormData || null);
      })
      .finally(() => {
        if (!active) return;
        setFormLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    application,
    activeTab,
    applicationCouncilSlug,
    applicationIdentity,
    formReloadIndex,
    hasFormSignal,
    inlineFormData,
    isOpen,
    safeReferenceNo,
  ]);

  const _persistReportToAccount = async (report, metadata, app) => {
    try {
      const safeRef = app?.reference_no || app?.ref || 'Unknown Ref';
      const safeAddr = app?.address || 'Unknown Address';

      await createReport({
        title: `AI Planning Report – ${safeAddr}`,
        kind: 'property',
        reference_no: safeRef,
        address: safeAddr,
        report_text: report,
        metadata: metadata
      });
    } catch (err) {
      console.warn('Persist report to account failed. Falling back to local cache.', err);
      // Keep existing local save (unchanged)
      await userDataService?.addReport?.({
        reference_no: app?.reference_no || app?.ref || 'Unknown Ref',
        report: report,
        address: app?.address || 'Unknown Address'
      });
    }
  };

  const _saveReportToStorage = (reportText) => {
    try {
      const existing = JSON.parse(localStorage.getItem('planning_reports') || '[]');
      const entry = {
        id: `${application.reference_no || application.ref}_${Date.now()}`,
        reference_no: application.reference_no || application.ref || 'unknown',
        address,
        report: reportText,
        generated_at: new Date().toISOString(),
        application_data: application,
      };
      const updated = [entry, ...existing];
      localStorage.setItem('planning_reports', JSON.stringify(updated));
      setPreviousReports(updated.filter(r => r.reference_no === entry.reference_no));
      window.dispatchEvent(new CustomEvent('reportsUpdated', { detail: updated }));
    } catch (_error) { void _error; }
  };

  const generateReport = async () => {
    if (sourceRecordOnly) return;
    setIsGeneratingReport(true);
    setReportError('');
    setGeneratedReport('');
    setReportPersistenceNotice(null);
    try {
      const data = await requestPlanningReport(application);
      setGeneratedReport(data.report);

      const persistResult = await userDataService.persistGeneratedReport({
        reference_no: application?.reference_no || application?.ref || 'Unknown Ref',
        address: application?.address || 'Unknown Address',
        report: data.report,
        application_data: application,
        metadata: data.metadata,
        title: `AI Planning Report - ${application?.address || 'Unknown Address'}`,
        kind: 'property',
      });

      setReportPersistenceNotice({
        syncState: persistResult.syncState,
        message: persistResult.message,
      });

      const updatedReports = userDataService
        .getCachedUserData()
        .reports
        .filter(report => report.reference_no === (application?.reference_no || application?.ref || 'unknown'));
      setPreviousReports(updatedReports);
    } catch (e) {
      setReportError(e?.message || 'Failed to generate report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const copyReport = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedReport(true);
      setTimeout(() => setCopiedReport(false), 1500);
    } catch (_error) { void _error; }
  };

  const downloadReport = (text, filename) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    a.download = `${filename}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadPDF = async (reportText, filename) => {
    try {
      const jsPDF = (await import('jspdf')).default;
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const maxLineWidth = pageWidth - margin * 2;
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Planning Report - ${qualifiedReferenceNo || 'N/A'}`, margin, 30);
      // Details
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      let y = 50;
      const details = [
        `Address: ${address}`,
        `Status: ${(application && application.status) || 'N/A'}`,
        `Generated: ${new Date().toLocaleDateString('en-GB')}`,
      ];
      details.forEach((d) => { pdf.text(d, margin, y); y += 6; });
      y += 10;
      // Content
      pdf.setFontSize(9);
      const lines = pdf.splitTextToSize(reportText, maxLineWidth);
      lines.forEach((line) => {
        if (y > pdf.internal.pageSize.getHeight() - 20) { pdf.addPage(); y = 20; }
        pdf.text(line, margin, y); y += 4;
      });
      pdf.save(`${filename}.pdf`);
    } catch (e) {
      console.error('Failed to generate PDF', e);
    }
  };

  const deletePrevReport = (reportId) => {
    try {
      const all = JSON.parse(localStorage.getItem('planning_reports') || '[]');
      const updated = all.filter(r => r.id !== reportId);
      localStorage.setItem('planning_reports', JSON.stringify(updated));
      setPreviousReports(updated.filter(r => r.reference_no === (application?.reference_no || application?.ref)));
      if (selectedPrevReportId === reportId) setSelectedPrevReportId(null);
      window.dispatchEvent(new CustomEvent('reportsUpdated', { detail: updated }));
    } catch (_error) { void _error; }
  };

  // Document handling
  const handleDocumentClick = async (doc) => {
    const documentUrl = getDocumentOpenUrl(doc);
    const loadingKey = doc?.thumbnail_url || doc?.thumbnail || documentUrl;

    trackApplicationDetailAction('document_thumbnail_opened', {
      placement: 'documents_tab',
      metadata: {
        has_document_url: Boolean(documentUrl),
        has_thumbnail: Boolean(doc?.thumbnail_url),
      },
    });

    if (documentUrl) {
      setLoadingDoc(loadingKey);
      try {
        await openPlanningDoc(documentUrl, application.link || application.portal_url || '', doc, handleImageOpen, {
          reference: safeReferenceNo,
          council: applicationCouncilSlug,
          title: doc?.source_desc || doc?.title || doc?.name || 'Planning document',
          previewUrl: doc?.thumbnail_url || doc?.thumbnail,
        });
      } finally {
        setLoadingDoc(null);
      }
    } else if (doc.thumbnail_url) {
      handleImageOpen(doc.thumbnail_url);
    }
  };

  const handleCouncilDocumentClick = async (doc) => {
    const documentUrl = getDocumentOpenUrl(doc);
    const appLink = application.link || application.portal_url || '';
    if (!documentUrl || !appLink) return;

    const primingKey = `prime:${documentUrl}`;
    setPrimingDoc(primingKey);
    try {
      await openPrimedPlanningDoc(documentUrl, appLink, doc, {
        reference: safeReferenceNo,
        council: applicationCouncilSlug,
        title: doc?.source_desc || doc?.title || doc?.name || 'Planning document',
        previewUrl: doc?.thumbnail_url || doc?.thumbnail,
      });
    } finally {
      setPrimingDoc(null);
    }
  };

  const nextDoc = () => {
    if (previewDocs.length > 1) {
      setDocDirection(1);
      setCurrentDocIndex((prev) => (prev + 1) % previewDocs.length);
    }
  };

  const prevDoc = () => {
    if (previewDocs.length > 1) {
      setDocDirection(-1);
      setCurrentDocIndex((prev) => (prev - 1 + previewDocs.length) % previewDocs.length);
    }
  };

  if (!isOpen || !application) return null;

  const modal = (
    <AnimatePresence>
      <motion.div
        className={renderInline
          ? "planning-register relative z-10 flex w-full justify-center px-2 sm:px-4"
          : "planning-register fixed inset-0 z-[7200] flex items-start md:items-center justify-center bg-[rgba(24,20,14,0.68)]"}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={renderInline ? undefined : handleBackdropClick}
        style={renderInline
          ? {
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--cs-nav-height, 64px) + 4px)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)'
          }
          : {
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
            paddingTop: 'max(env(safe-area-inset-top), 8px)',
            paddingBottom: 'max(env(safe-area-inset-bottom), 8px)'
          }}
      >
        <motion.div
          ref={modalRef}
          className={`relative max-w-6xl ${renderInline ? 'w-full mx-0' : 'h-[100dvh] w-full rounded-none sm:h-[90vh] sm:w-[calc(100%-2rem)]'} bg-[var(--c-surface)] border border-[var(--c-border)] border-t-[3px] border-t-[var(--c-text-primary)] overflow-hidden flex flex-col`}
          initial={{ opacity: 0, y: 24 }}
          animate={{
            opacity: isClosing ? 0 : 1,
            y: isClosing ? 24 : 0,
          }}
          exit={{ opacity: 0, y: 24 }}
          transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1],
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            boxShadow: '0 24px 60px rgba(20, 16, 10, 0.28)',
            ...(renderInline
              ? {
                height: 'calc(100dvh - env(safe-area-inset-top, 0px) - var(--cs-nav-height, 64px) - env(safe-area-inset-bottom, 0px) - 16px)',
                maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - var(--cs-nav-height, 64px) - env(safe-area-inset-bottom, 0px) - 16px)'
              }
              : {})
          }}
        >
          {/* Header — collapses to a compact bar on scroll */}
          <motion.div
            className={`relative z-10 border-b border-[var(--c-border)] bg-[var(--c-surface)] transition-[padding] duration-300 ease-out ${
              isContentScrolled ? 'px-3 py-2 sm:px-6 sm:py-2.5' : 'px-3 py-3 sm:px-6 sm:py-5'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Category icon */}
              {categoryInfo && (
                <motion.div
                  className="mt-0.5 flex-shrink-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <span
                    className={`inline-flex items-center justify-center border border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-secondary)] transition-[height,width] duration-300 ease-out ${
                      isContentScrolled ? 'h-9 w-9' : 'h-10 w-10 sm:h-12 sm:w-12'
                    }`}
                  >
                    <CategoryIcon
                      categoryId={categoryInfo.id || application?.category || ''}
                      size={isContentScrolled ? 22 : 28}
                      className="text-[var(--c-text-secondary)]"
                    />
                  </span>
                </motion.div>
              )}

              {/* Title block */}
              <div className="min-w-0 flex-1">
                {/* Kicker: reference + register line, collapses out when scrolled */}
                <motion.p
                  className="pr-kicker flex flex-wrap items-center gap-x-2 overflow-hidden whitespace-nowrap"
                  animate={{
                    height: isContentScrolled ? 0 : 'auto',
                    opacity: isContentScrolled ? 0 : 1,
                    marginBottom: isContentScrolled ? 0 : 4,
                  }}
                  transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                >
                  {/* Phones show just the reference so it never truncates mid-word. */}
                  <span className={`text-[var(--c-accent)] ${safeReferenceNo ? 'hidden sm:inline' : ''}`}>Planning application</span>
                  {safeReferenceNo && (
                    <>
                      <span aria-hidden="true" className="hidden sm:inline">&middot;</span>
                      <span className="text-[var(--c-text-primary)]">{qualifiedReferenceNo}</span>
                    </>
                  )}
                </motion.p>

                <motion.h1
                  className={`font-['Newsreader'] font-medium tracking-[-0.01em] text-[var(--c-text-primary)] transition-[font-size,line-height] duration-300 ease-out ${
                    isContentScrolled
                      ? 'text-[15px] leading-[1.25] sm:text-lg'
                      : 'text-lg leading-[1.15] sm:text-[26px]'
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  title={address}
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: isContentScrolled ? 1 : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {address}
                </motion.h1>

                {/* Meta row collapses out when scrolled */}
                <motion.div
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 overflow-hidden text-[11px]"
                  animate={{
                    height: isContentScrolled ? 0 : 'auto',
                    opacity: isContentScrolled ? 0 : 1,
                    marginTop: isContentScrolled ? 0 : 8,
                  }}
                  transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                >
                  {/* Status — dot + small caps, no pill */}
                  {application?.status && (
                    <span className="pr-status">
                      <span className="pr-status-dot" style={{ background: statusStyle.dotColor }} aria-hidden="true" />
                      {application.status}
                    </span>
                  )}
                  {isWorthWatching && (
                    <span
                      className="pr-status !text-[var(--c-accent)]"
                      title={application?.interest_reason || 'Worth watching'}
                      aria-label="Worth watching"
                    >
                      <Flag className="h-3 w-3 shrink-0" />
                      <span className="min-w-0 truncate">Worth watching</span>
                    </span>
                  )}
                  {/* Category */}
                  {categoryInfo?.label && (
                    <span className="pr-data">{categoryInfo.label}</span>
                  )}
                  {/* Combined badge */}
                  {application?.ai_match_data?.is_combined_application && (
                    <span className="pr-status !text-[var(--c-accent)]">
                      <span className="pr-status-dot bg-[var(--c-accent)]" aria-hidden="true" />
                      Combined
                      {application.ai_match_data.development_name && (
                        <span className="ml-1 max-w-[140px] truncate normal-case tracking-normal">
                          · {application.ai_match_data.development_name}
                        </span>
                      )}
                    </span>
                  )}
                  {/* Related-apps pager — compact */}
                  {relatedApplications.length > 1 && (
                    <span
                      className="inline-flex items-center overflow-hidden border border-[var(--c-border)] bg-[var(--c-bg)]"
                      aria-label={`Application ${currentRelatedIndex + 1} of ${relatedApplications.length}`}
                    >
                      <button
                        type="button"
                        onClick={() => goToRelated(-1)}
                        className="flex h-6 w-6 items-center justify-center border-r border-[var(--c-border)] text-[var(--c-text-secondary)] transition-colors hover:bg-[var(--c-surface-hover)] hover:text-[var(--c-text-primary)]"
                        aria-label="Previous related application"
                        title="Previous (←)"
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </button>
                      <span className="pr-data px-2 !text-[10px] !text-[var(--c-text-primary)]">
                        {currentRelatedIndex + 1}/{relatedApplications.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => goToRelated(1)}
                        className="flex h-6 w-6 items-center justify-center border-l border-[var(--c-border)] text-[var(--c-text-secondary)] transition-colors hover:bg-[var(--c-surface-hover)] hover:text-[var(--c-text-primary)]"
                        aria-label="Next related application"
                        title="Next (→)"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </motion.div>
              </div>

              {/* Action cluster — unified icon-button styling */}
              <div className="flex flex-shrink-0 items-center gap-1.5">
                {/* Council portal link — primary outbound CTA, hidden on small screens */}
                {application?.link && application.link !== '#' && (
                  <motion.a
                    href={application.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackApplicationDetailAction('council_record_opened', {
                      placement: 'modal_header',
                      metadata: { destination: 'council_portal' },
                    })}
                    className="hidden h-11 items-center gap-1.5 border border-[var(--c-border)] bg-[var(--c-bg)] px-3 font-['DM_Mono'] text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-text-tertiary)] hover:text-[var(--c-accent)] sm:inline-flex"
                    whileTap={{ scale: 0.98 }}
                    title="Open on council portal"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Council portal
                  </motion.a>
                )}

                {onToggleFavorite && (
                  <motion.button
                    type="button"
                    onClick={() => {
                      trackApplicationDetailAction('save_clicked', {
                        placement: 'modal_favourite',
                        status: isFavorite ? 'remove_requested' : 'save_requested',
                      });
                      onToggleFavorite();
                    }}
                    className={`inline-flex h-11 w-11 items-center justify-center border transition-colors ${
                      isFavorite
                        ? 'border-[var(--c-error)] bg-[var(--c-bg)] text-[var(--c-error)]'
                        : 'border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-secondary)] hover:border-[var(--c-text-tertiary)] hover:text-[var(--c-accent)]'
                    }`}
                    whileTap={{ scale: 0.95 }}
                    aria-label={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
                    title={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
                  >
                    <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                  </motion.button>
                )}

                <ShareButton application={shareApplication} documents={shareDocuments} shareUrl={modalShareUrl} />

                <motion.button
                  onClick={() => handleClose('close_button')}
                  title={renderInline ? 'Back to Planning Updates' : 'Close'}
                  aria-label={renderInline ? 'Back to Planning Updates' : 'Close'}
                  className={`inline-flex h-11 w-11 items-center justify-center border border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-secondary)] transition-colors ${
                    renderInline ? 'hover:border-[var(--c-text-tertiary)] hover:text-[var(--c-accent)]' : 'hover:border-[var(--c-text-tertiary)] hover:text-[var(--c-error)]'
                  }`}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderInline ? <ChevronLeft className="h-4 w-4" /> : <X className="h-4 w-4" />}
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Tab Navigation — register index: mono small caps with ink underline */}
          <motion.div
            className="relative border-b border-[var(--c-border)] bg-[var(--c-surface)] px-2 sm:px-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div
              ref={tabListRef}
              role="tablist"
              aria-label="Planning application sections"
              className="pr-record-tabs scrollbar-hide scroll-smooth px-12 sm:px-0"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {modalTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTabWithDirection(tab.id)}
                    className="pr-record-tab group"
                  >
                    <span className={`transition-colors ${isActive ? 'text-[var(--c-accent)]' : 'text-[var(--c-text-tertiary)] group-hover:text-[var(--c-text-secondary)]'}`}>
                      {tab.icon}
                    </span>
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="inline sm:hidden">
                      {useFullTabLabels ? tab.label : (tab.shortLabel || tab.label)}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => scrollTabList(-1)}
              aria-label="Scroll application tabs left"
              className="absolute left-1 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)] opacity-95 transition hover:border-[var(--c-text-tertiary)] hover:text-[var(--c-accent)] sm:hidden"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollTabList(1)}
              aria-label="Scroll application tabs right"
              className="absolute right-1 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)] opacity-95 transition hover:border-[var(--c-text-tertiary)] hover:text-[var(--c-accent)] sm:hidden"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[var(--c-surface)] via-[var(--c-surface)]/80 to-transparent sm:hidden" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[var(--c-surface)] via-[var(--c-surface)]/80 to-transparent sm:hidden" />
          </motion.div>

          {/* Content */}
          <motion.div
            ref={contentScrollRef}
            onScroll={handleContentScroll}
            className="flex-1 overflow-y-auto overflow-x-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            style={{
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <div className="px-4 sm:px-6">
              <CouncilCoverageNotice councilSlug={applicationCouncilSlug} />
              {reportedDecisionNotice && (
                <p role="note" data-testid="reported-decision-notice" className="my-3 border-l-2 border-[var(--c-accent)] pl-3 text-sm leading-relaxed text-[var(--c-text-secondary)]">
                  {reportedDecisionNotice}
                  {' '}Source report captured{' '}
                  <time dateTime={application.reported_source_facts.source_captured_at}>
                    {new Date(application.reported_source_facts.source_captured_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                  </time>.
                </p>
              )}
              {sourceRefreshNotice && (
                <p role="status" data-testid="source-refresh-notice" className="my-3 border-l-2 border-[var(--c-accent)] pl-3 text-sm leading-relaxed text-[var(--c-text-secondary)]">
                  {sourceRefreshNotice}
                  {' '}Last checked{' '}
                  <time dateTime={application.source_refresh.last_checked_at}>
                    {new Date(application.source_refresh.last_checked_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                  </time>.
                </p>
              )}
            </div>
            <AnimatePresence mode="wait" custom={tabDirection} initial={false}>
            {/* Full-width layout for Form Data tab */}
            {activeTab === 'form' ? (
              <motion.div
                key="form-layout"
                className="w-full"
                custom={tabDirection}
                variants={tabPanelVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <ApplicationFormPanel
                  application={application}
                  formData={formData || inlineFormData}
                  isLoading={formLoading}
                  error={formError}
                  onRetry={() => setFormReloadIndex((prev) => prev + 1)}
                />
              </motion.div>
            ) : activeTab === 'map' && showMapTab ? (
              <motion.div
                key="map-layout"
                custom={tabDirection}
                variants={tabPanelVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="w-full p-4 sm:p-6"
              >
                <PlanningArticleMap
                  application={application}
                  variant="modal"
                  className="mx-auto w-full max-w-6xl"
                />
              </motion.div>
            ) : activeTab === 'journal' && normalizedPublishedArticle ? (
              <motion.div
                key="journal-layout"
                custom={tabDirection}
                variants={tabPanelVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="w-full p-4 sm:p-6"
              >
                <article className="mx-auto w-full max-w-[840px]">
                  <div className="border-b border-[var(--c-border)] pb-7">
                    <div className="inline-flex border border-[var(--c-accent)]/45 px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[0.2em] text-[var(--c-accent)]">
                      City Scope Journal
                    </div>
                    <h2 className="mt-4 font-['Newsreader'] text-3xl font-medium leading-[1.08] tracking-[-0.01em] text-[var(--c-text-primary)] sm:text-4xl">
                      {normalizedPublishedArticle.title}
                    </h2>
                    {normalizedPublishedArticle.excerpt && (
                      <p className="mt-4 max-w-3xl text-lg leading-relaxed text-[var(--c-text-secondary)]">
                        {normalizedPublishedArticle.excerpt}
                      </p>
                    )}
                    <div className="mt-5 flex flex-wrap gap-2 font-['DM_Mono'] text-[11px] uppercase tracking-[0.12em] text-[var(--c-text-secondary)]">
                      {normalizedPublishedArticle.publishedAt ? <span>{formatDate(normalizedPublishedArticle.publishedAt)}</span> : null}
                      {normalizedPublishedArticle.publishedAt && normalizedPublishedArticle.readingTime ? <span>·</span> : null}
                      {normalizedPublishedArticle.readingTime ? <span>{normalizedPublishedArticle.readingTime} min read</span> : null}
                      <span>·</span>
                      <span>{qualifiedReferenceNo}</span>
                    </div>
                  </div>

                  <div className="space-y-7 py-8">
                    {renderModalJournalSectionsWithPhotos(
                      normalizedPublishedArticle.sections || [],
                      getPlanningArticlePhotoItems(application, 3)
                    )}
                  </div>
                </article>
              </motion.div>
            ) : (
              /* Two-column layout for all other tabs */
              <motion.div
                key="detail-layout"
                layout
                custom={tabDirection}
                variants={tabPanelVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className={`grid gap-4 overflow-x-hidden p-3 sm:gap-6 sm:p-6 ${useExpandedDetailLayout ? 'lg:grid-cols-1' : 'lg:grid-cols-2'}`}
                transition={{
                  layout: { type: 'spring', stiffness: 280, damping: 30, mass: 0.9 },
                }}
              >
                {/* Left Column - Image and Documents */}
                <motion.div
                  layout
                  className="space-y-6 min-w-0"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: 0.3,
                    duration: 0.5,
                    layout: { type: 'spring', stiffness: 300, damping: 28, mass: 0.86 },
                  }}
                >
                {/* Main Image */}
                {currentDoc || isMapSnapshot ? (
                  <div
                    className={`relative group ${
                      showExpandedOverviewHeadlines
                        ? 'rounded-xl border border-[var(--c-accent)]/35 bg-[var(--c-surface)]/70 p-2 shadow-[0_22px_70px_rgba(0,0,0,0.20)] lg:grid lg:grid-cols-[minmax(0,1fr)_260px] lg:items-stretch lg:gap-4 xl:grid-cols-[minmax(0,1fr)_286px]'
                        : ''
                    }`}
                  >
                    <motion.div
                      layout
                      animate={{
                        scale: isImageExpanded ? [1, 1.018, 0.997, 1] : [1, 0.985, 1.006, 1],
                        boxShadow: isImageExpanded
                          ? '0 26px 90px rgba(0,0,0,0.38), 0 0 0 1px rgba(231,201,72,0.18)'
                          : '0 0 0 rgba(0,0,0,0), 0 0 0 1px rgba(231,201,72,0)',
                      }}
                      transition={{
                        layout: { type: 'spring', stiffness: 300, damping: 22, mass: 0.82 },
                        scale: {
                          duration: isImageExpanded ? 0.52 : 0.42,
                          times: [0, 0.45, 0.74, 1],
                          ease: [0.22, 1, 0.36, 1],
                        },
                        boxShadow: { duration: 0.28, ease: 'easeOut' },
                      }}
                      className={`relative transform-gpu bg-[var(--c-bg)] rounded-lg overflow-hidden border border-[var(--c-border)] will-change-transform ${
                        isImageExpanded
                          ? 'h-[min(72vh,760px)] min-h-[320px] sm:min-h-[460px]'
                          : 'aspect-video'
                      }`}
                      style={{ transformOrigin: 'center center' }}
                    >
                      <AnimatePresence initial={false} custom={docDirection} mode="sync">
                        <motion.div
                          key={`${currentMediaPreviewUrl || currentDocUrl || 'doc'}-${currentDocIndex}`}
                          className="absolute inset-0"
                          variants={docPageVariants}
                          custom={docDirection}
                          initial="enter"
                          animate="center"
                          exit="exit"
                        >
                          <Image
                            src={currentMediaPreviewUrl}
                            alt={isMapSnapshot ? 'Location map' : 'Planning document'}
                            fill
                            className="object-contain"
                            onError={(e) => {
                              const image = e.currentTarget;
                              if (image.dataset.documentPlaceholderApplied === 'true') return;
                              image.dataset.documentPlaceholderApplied = 'true';
                              image.src = DOCUMENT_PREVIEW_PLACEHOLDER;
                            }}
                          />
                        </motion.div>
                      </AnimatePresence>
                      {isMapSnapshot && (
                        <div className="pointer-events-none absolute bottom-2 left-2 z-20 border border-white/60 bg-white/90 px-2 py-1 text-[10px] leading-none text-black shadow-sm">
                          {MAP_SNAPSHOT_ATTRIBUTION}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsImageExpanded((expanded) => !expanded)}
                        className="absolute inset-0 z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)] focus-visible:ring-inset"
                        style={{ cursor: buildDocumentPreviewCursor(isImageExpanded) }}
                        aria-label={isImageExpanded ? 'Collapse preview' : (isMapSnapshot ? 'Expand location map' : 'Expand document preview')}
                        aria-describedby={documentPreviewTooltipId}
                      />
                      <div
                        id={documentPreviewTooltipId}
                        role="tooltip"
                        className="pointer-events-none absolute left-1/2 top-3 z-30 -translate-x-1/2 border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1.5 font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-secondary)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
                      >
                        {documentPreviewTooltipText}
                      </div>
                      {/* Navigation arrows */}
                      {previewDocs.length > 1 && (
                        <>
                          <button
                            onClick={prevDoc}
                            className="absolute left-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-[var(--c-accent)] transition-colors hover:bg-black/75"
                            aria-label="Previous page"
                            title="Previous page"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            onClick={nextDoc}
                            className="absolute right-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-[var(--c-accent)] transition-colors hover:bg-black/75"
                            aria-label="Next page"
                            title="Next page"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </>
                      )}

                      {/* Document actions */}
                      <div className="absolute bottom-2 right-2 z-20 flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setIsImageExpanded((expanded) => !expanded)}
                          className="flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
                          aria-label={isImageExpanded ? 'Collapse preview' : (isMapSnapshot ? 'Expand location map' : 'Expand document preview')}
                          title={isImageExpanded ? 'Collapse preview' : 'Expand preview'}
                        >
                          {isImageExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </button>
                        {currentDocUrl && (
                          <button
                            onClick={() => handleDocumentClick(currentDoc)}
                            disabled={loadingDoc === currentDocLoadingKey}
                            className="flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 disabled:opacity-50"
                            aria-label="Open document in City Scope"
                            title="Open in City Scope"
                          >
                            {loadingDoc === currentDocLoadingKey ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                              />
                            ) : (
                              <ExternalLink className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {currentDocCanPrime && (
                          <button
                            onClick={() => handleCouncilDocumentClick(currentDoc)}
                            disabled={primingDoc === currentDocPrimingKey}
                            className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--c-accent)] text-black transition-colors hover:brightness-110 disabled:opacity-50"
                            aria-label="Open council PDF"
                            title="Open council PDF"
                          >
                            {primingDoc === currentDocPrimingKey ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                              />
                            ) : (
                              <FileText className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Document counter */}
                      {previewDocs.length > 1 && (
                        <div className="absolute top-2 left-2 z-20 px-2 py-1 bg-black/50 rounded text-white text-xs">
                          {currentDocIndex + 1} / {previewDocs.length}
                        </div>
                      )}
                    </motion.div>
                    <AnimatePresence>
                      {showExpandedOverviewHeadlines && (
                        <motion.aside
                          data-testid="expanded-overview-info-flags"
                          className="mt-3 grid gap-2 sm:grid-cols-2 lg:mt-0 lg:flex lg:flex-col lg:justify-start lg:gap-3 lg:overflow-visible lg:py-6 lg:pr-1"
                          initial={{ opacity: 0, x: 22, filter: 'blur(6px)' }}
                          animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, x: 16, filter: 'blur(4px)' }}
                          transition={{ type: 'spring', stiffness: 300, damping: 26, mass: 0.82 }}
                          aria-label="Overview headlines for this planning application"
                        >
                          {expandedOverviewFlags.map((flag, index) => {
                            const FlagIcon = flag.icon || Flag;
                            return (
                              <motion.button
                                key={flag.id}
                                type="button"
                                onClick={() => handleExpandedOverviewFlagClick(flag)}
                                className="group relative flex min-h-[66px] items-center gap-3 rounded-lg border border-[var(--c-accent)]/35 bg-[var(--c-bg)]/95 px-3.5 py-3 text-left shadow-[0_12px_30px_rgba(0,0,0,0.14)] backdrop-blur-md transition-colors hover:border-[var(--c-accent)] hover:bg-[var(--c-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)] lg:min-h-[72px] lg:rounded-r-sm lg:pr-4"
                                initial={{ opacity: 0, x: 24, y: 8 }}
                                animate={{ opacity: 1, x: 0, y: 0 }}
                                exit={{ opacity: 0, x: 14 }}
                                transition={{ delay: index * 0.045, type: 'spring', stiffness: 360, damping: 25, mass: 0.74 }}
                                whileHover={{ x: -5, scale: 1.015 }}
                                whileTap={{ scale: 0.985 }}
                                title={`${flag.label}: ${flag.value}`}
                              >
                                <span className="pointer-events-none absolute -right-[9px] top-1/2 hidden h-4 w-4 -translate-y-1/2 rotate-45 border-r border-t border-[var(--c-accent)]/35 bg-[var(--c-bg)]/95 transition-colors group-hover:border-[var(--c-accent)] group-hover:bg-[var(--c-surface)] lg:block" aria-hidden="true" />
                                {flag.id === 'status' && (
                                  <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-[var(--c-accent)] shadow-[0_0_0_4px_rgba(231,201,72,0.14)]" aria-hidden="true" />
                                )}
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/10 text-[var(--c-accent)] lg:h-11 lg:w-11">
                                  <FlagIcon className="h-5 w-5" />
                                </span>
                                <span className="min-w-0 pr-3">
                                  <span className="block font-['DM_Mono'] text-[9px] uppercase leading-none tracking-[0.08em] text-[var(--c-accent)]">
                                    {flag.label}
                                  </span>
                                  <span className="mt-1.5 block truncate text-sm font-semibold leading-tight text-[var(--c-text-primary)]">
                                    {flag.value}
                                  </span>
                                </span>
                              </motion.button>
                            );
                          })}
                        </motion.aside>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="flex aspect-video flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--c-border)] bg-[var(--c-bg)] text-center">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-tertiary)]">
                      <FileText className="h-5 w-5" />
                    </span>
                    <p className="text-sm font-medium text-[var(--c-text-primary)]">No documents shown on CityScope</p>
                    <p className="max-w-[260px] text-[11px] text-[var(--c-text-tertiary)]">
                      {documentListNotice || 'Check the council portal for drawings and supporting documents.'}
                    </p>
                  </div>
                )}

                {/* Document thumbnails */}
                {previewDocs.length > 1 && (
                  <div className="grid grid-cols-4 gap-3">
                    {previewDocs.map((doc, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (index !== currentDocIndex) {
                            setDocDirection(index > currentDocIndex ? 1 : -1);
                            setCurrentDocIndex(index);
                          }
                        }}
                        className={`pr-doc-thumb ${index === currentDocIndex ? 'is-active' : ''}`}
                      >
                        <Image
                          src={getDocumentPreviewUrl(doc)}
                          alt={`Document ${index + 1}`}
                          fill
                          className="object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === 'overview' && normalizedPublishedArticle && (
                  <motion.div
                    className="pr-record-card hidden overflow-hidden lg:block"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.16, type: 'spring', stiffness: 240, damping: 24 }}
                  >
                    <div className="border-b border-[var(--c-border)] px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[var(--c-accent)]" />
                        <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.18em] text-[var(--c-accent)]">
                          Journal
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3 p-4">
                      <h3 className="font-['Newsreader'] text-lg font-medium leading-tight text-[var(--c-text-primary)]">
                        {normalizedPublishedArticle.title}
                      </h3>
                      {normalizedPublishedArticle.excerpt && (
                        <p className="line-clamp-3 text-sm leading-relaxed text-[var(--c-text-secondary)]">
                          {normalizedPublishedArticle.excerpt}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => setActiveTabWithDirection('journal', 'journal_teaser_click')}
                        className="pr-button inline-flex items-center gap-2"
                      >
                        Open journal
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Right Column - Tabs (Overview / Reports) */}
              <motion.div
                key="detail-companion-column"
                data-testid="detail-companion-column"
                aria-hidden={isImageExpanded}
                className={`${useExpandedDetailLayout ? 'hidden' : 'min-w-0 overflow-hidden space-y-6'}`}
                variants={detailCompanionVariants}
                initial="enter"
                animate={isImageExpanded ? 'exit' : 'visible'}
                style={{ pointerEvents: isImageExpanded ? 'none' : 'auto' }}
              >
                            <AnimatePresence mode="wait" custom={tabDirection} initial={false}>

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                    <motion.div
                      key="overview"
                      className="space-y-5"
                      custom={tabDirection}
                      variants={tabPanelVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                    >
                {/* Skeleton state while hydrating sparse detail */}
                {isHydratingDetail && !application.description && !application.ai_summary && (
                  <div className="space-y-4" aria-busy="true" aria-live="polite">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {[0, 1].map((i) => (
                        <div
                          key={i}
                          className="animate-pulse pr-record-card"
                        >
                          <div className="mb-2 h-3 w-20 rounded-full bg-[var(--c-border)]/70" />
                          <div className="h-5 w-32 rounded-md bg-[var(--c-border)]/60" />
                        </div>
                      ))}
                    </div>
                    <div className="animate-pulse pr-record-card">
                      <div className="mb-3 h-3 w-24 rounded-full bg-[var(--c-border)]/70" />
                      <div className="space-y-2">
                        <div className="h-3 w-full rounded-md bg-[var(--c-border)]/55" />
                        <div className="h-3 w-[92%] rounded-md bg-[var(--c-border)]/55" />
                        <div className="h-3 w-[78%] rounded-md bg-[var(--c-border)]/55" />
                        <div className="h-3 w-[60%] rounded-md bg-[var(--c-border)]/55" />
                      </div>
                    </div>
                  </div>
                )}
                {/* Key dates */}
                <motion.div
                  data-overview-section="dates"
                  className={`pr-record-fact-grid pr-record-fact-grid--2 ${application.validated_date ? '' : '!grid-cols-1'}`}
                  variants={overviewCardStagger}
                  initial="hidden"
                  animate="show"
                >
                  <motion.div variants={overviewCardItem} className="pr-record-fact" transition={{ type: 'spring', stiffness: 280, damping: 24 }}>
                    <div className="pr-record-fact-label">
                      <Calendar className="h-3.5 w-3.5" />
                      Received
                    </div>
                    <p className="pr-record-fact-value">
                      {formatDate(application.received_date)}
                    </p>
                  </motion.div>

                  {application.validated_date && (
                    <motion.div variants={overviewCardItem} className="pr-record-fact" transition={{ type: 'spring', stiffness: 280, damping: 24 }}>
                      <div className="pr-record-fact-label">
                        <CheckCircle className="h-3.5 w-3.5" />
                        Validated
                      </div>
                      <p className="pr-record-fact-value">
                        {formatDate(application.validated_date)}
                      </p>
                    </motion.div>
                  )}
                </motion.div>

                <CouncilDecisionFacts application={application} card />

                {normalizedPublishedArticle && (
                  <motion.div
                    className="pr-record-card overflow-hidden lg:hidden"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08, type: 'spring', stiffness: 240, damping: 24 }}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveTabWithDirection('journal', 'journal_teaser_click')}
                      className="block w-full p-3 text-left transition-colors hover:bg-[var(--c-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)] focus-visible:ring-inset"
                    >
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-[var(--c-accent)]" />
                          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.16em] text-[var(--c-accent)]">
                            Journal
                          </span>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--c-accent)]">
                          Open
                          <ChevronRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                      <h3 className="line-clamp-2 font-['Newsreader'] text-sm font-medium leading-snug text-[var(--c-text-primary)]">
                        {normalizedPublishedArticle.title}
                      </h3>
                      {normalizedPublishedArticle.excerpt && (
                        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-[var(--c-text-secondary)]">
                          {normalizedPublishedArticle.excerpt}
                        </p>
                      )}
                    </button>
                  </motion.div>
                )}

                <div data-overview-section="signals">
                  <PlanningSignalsPanel
                    application={application}
                    category={categoryInfo}
                    initialVisible={6}
                    onOpenDetails={hasPlanningSignals ? () => setActiveTabWithDirection('signals', 'signals_summary_click') : undefined}
                  />
                </div>

                {/* Application Insights */}
                {showFormTab && (
                  <div data-overview-section="insights">
                    <ApplicationInsightsCard
                      insights={overviewNormalizedForm.fields}
                      coverage={overviewFormSummary.totalFields > 0 ? overviewFormSummary.coverage : null}
                      filledFields={overviewFormSummary.filledFields}
                      totalFields={overviewFormSummary.totalFields}
                      isLoading={formLoading && overviewFormSummary.totalFields === 0}
                      onOpenFormData={({ fieldKeyBase: _fieldKeyBase } = {}) => {
                        setActiveTabWithDirection('form', 'application_insights_click');
                        // Future: pass fieldKeyBase to ApplicationFormPanel for highlighting
                      }}
                      onViewPdf={application?.form_pdf_url ? () => {
                        trackApplicationDetailAction('application_form_opened', {
                          placement: 'application_insights_pdf',
                          metadata: { destination: 'form_pdf' },
                        });
                        if (typeof window !== 'undefined') {
                          window.open(application.form_pdf_url, '_blank', 'noopener,noreferrer');
                        }
                      } : undefined}
                    />
                  </div>
                )}
                {/* Description / AI Summary */}
                <motion.div
                  data-overview-section="summary"
                  className="pr-record-card"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08, type: 'spring', stiffness: 240, damping: 24 }}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[var(--c-text-tertiary)]" />
                      <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--c-text-tertiary)]">
                        {application?.description_completeness === 'official_search_summary' ? 'Official search summary' : showAiSummaries && application.ai_summary ? 'AI summary' : 'Description'}
                      </span>
                    </div>
                    {application?.ai_summary && description && (
                      <motion.button
                        type="button"
                        onClick={() => setShowOriginalOverview((v) => !v)}
                        className="inline-flex min-h-11 items-center gap-1 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3 text-[11px] font-medium text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)]/35 hover:text-[var(--c-accent)]"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        {showOriginalOverview ? 'Hide original' : 'View original'}
                      </motion.button>
                    )}
                  </div>

                  {application?.ai_summary && description && showOriginalOverview && (
                    <motion.div
                    className="pr-record-card--accent pr-record-card mb-3"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--c-text-tertiary)]" />
                        <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--c-text-tertiary)]">Original description</span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--c-text-primary)]">{description}</p>
                    </motion.div>
                  )}

                  <p className="whitespace-pre-wrap leading-relaxed text-[var(--c-text-primary)]">
                    {displayDescription}
                  </p>
                </motion.div>

                {/* Combined development info */}
                {application.ai_match_data?.is_combined_application && (
                  <motion.div
                    className="pr-record-card pr-record-card--accent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, type: 'spring', stiffness: 240, damping: 24 }}
                  >
                    <div className="mb-3 flex items-center gap-2">
                      <Building className="h-4 w-4 text-[var(--c-accent)]" />
                      <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--c-accent)]">Combined development</span>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs text-[var(--c-text-tertiary)]">Development name</span>
                        <p className="mt-0.5 text-base font-semibold text-[var(--c-text-primary)]">{application.ai_match_data.development_name}</p>
                      </div>
                      {application.ai_match_data.development_aliases && application.ai_match_data.development_aliases.length > 0 && (
                        <div>
                          <span className="text-xs text-[var(--c-text-tertiary)]">Also known as</span>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {application.ai_match_data.development_aliases.map((alias, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center rounded-full border border-[var(--c-border)] bg-[var(--c-bg)] px-2.5 py-1 text-xs text-[var(--c-text-secondary)]"
                              >
                                {alias}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <p className="border-t border-[var(--c-accent)]/20 pt-2 text-xs text-[var(--c-text-secondary)]">
                        This application covers part of a larger development. Related applications appear in the “Applications” tab.
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Impact scale */}
                {application.scale_rating && (
                  <motion.div
                    data-overview-section="impact"
                    className="pr-record-card"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12, type: 'spring', stiffness: 240, damping: 24 }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <span className="pr-kicker">Impact scale</span>
                        <p className="mt-1 text-[12px] leading-snug text-[var(--c-text-secondary)]">
                          Estimated scale of impact on the surrounding area.
                        </p>
                      </div>
                      <span className="pr-numeral text-[var(--c-accent)]">
                        {application.scale_rating}/10
                      </span>
                    </div>
                    <div className="pr-impact-meter" aria-hidden="true">
                      {[...Array(10)].map((_, i) => {
                        const filled = i < application.scale_rating;
                        const isHigh = filled && application.scale_rating >= 8;
                        return (
                          <div
                            key={i}
                            className={`pr-impact-meter-segment ${filled ? 'is-filled' : ''} ${isHigh ? 'is-high' : ''}`}
                          />
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Top Comments preview */}
                {discussionsEnabled && (
                  <motion.div
                    className="pr-record-card overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.14, type: 'spring', stiffness: 240, damping: 24 }}
                  >
                    {/* Card header */}
                    <div className="flex items-center justify-between gap-3 border-b border-[var(--c-border)] px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MessageSquareText className="w-4 h-4 text-[var(--c-accent)]" />
                        <span className="text-xs font-mono text-[var(--c-text-secondary)]">DISCUSSION</span>
                        {discussionCommentCount > 0 && (
                          <span className="rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2 py-0.5 text-[10px] font-semibold text-[var(--c-text-secondary)]">
                            {discussionCommentCount}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTabWithDirection('discussion', 'discussion_prompt_click')}
                        className="inline-flex min-h-11 items-center gap-1 rounded-full border border-[var(--c-accent)]/35 bg-[var(--c-accent)]/10 px-3 text-[11px] font-semibold text-[var(--c-accent)] transition-colors hover:bg-[var(--c-accent)]/20"
                      >
                        {discussionPreviewComments.length > 0 ? 'View all' : 'Start a discussion'}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Comment previews or empty state */}
                    {discussionPreviewComments.length > 0 ? (
                      <ul className="divide-y divide-[var(--c-border)]/60">
                        {discussionPreviewComments.slice(0, 3).map((preview) => {
                          const initials = (() => {
                            const parts = String(preview.author?.displayName || '').trim().split(/\s+/).filter(Boolean);
                            if (parts.length === 0) return 'CS';
                            return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');
                          })();
                          const relativeTs = (() => {
                            if (!preview.createdAt) return '';
                            const diffMs = Date.now() - new Date(preview.createdAt).getTime();
                            const diffMin = Math.round(diffMs / 60_000);
                            if (diffMin < 1) return 'Just now';
                            if (diffMin < 60) return `${diffMin}m ago`;
                            const diffHr = Math.round(diffMin / 60);
                            if (diffHr < 24) return `${diffHr}h ago`;
                            const diffDay = Math.round(diffHr / 24);
                            return diffDay < 7 ? `${diffDay}d ago` : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(preview.createdAt));
                          })();
                          return (
                            <li key={preview.id} className="flex gap-3 px-4 py-4">
                              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[var(--c-accent)]/30 bg-[var(--c-accent)]/12 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--c-accent)]">
                                {initials}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                  <span className="text-[13px] font-semibold text-[var(--c-text-primary)]">
                                    {preview.author?.displayName || 'Community member'}
                                  </span>
                                  {relativeTs && (
                                    <span className="text-[11px] text-[var(--c-text-tertiary)]">{relativeTs}</span>
                                  )}
                                </div>
                                <p className="mt-1.5 line-clamp-2 text-[13px] leading-[1.55] text-[var(--c-text-secondary)]">
                                  {preview.body}
                                </p>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-tertiary)]">
                          <MessageSquareText className="w-4 h-4" />
                        </span>
                        <p className="text-xs font-medium text-[var(--c-text-primary)]">No comments yet</p>
                        <p className="max-w-[220px] text-[11px] leading-snug text-[var(--c-text-tertiary)]">
                          Be the first to share your local perspective on this application.
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}

                    </motion.div>
                  )}


              {/* Applications Tab */}
              {activeTab === 'applications' && relatedApplications.length > 1 && (
                    <motion.div
                      key="applications"
                      custom={tabDirection}
                      variants={tabPanelVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="p-4 sm:p-6"
                    >
                      <section className="overflow-hidden rounded-[22px] border border-[var(--c-border)] bg-[var(--c-surface)]">
                        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--c-border)] bg-[var(--c-bg)]/55 px-4 py-3.5 sm:px-5">
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-accent)]">
                              <Building className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <h3 className="text-base font-semibold leading-tight text-[var(--c-text-primary)]">
                                Related applications
                              </h3>
                              <p className="mt-1 max-w-xl text-[11px] leading-snug text-[var(--c-text-tertiary)]">
                                Same-address records kept together for comparison and source checking.
                              </p>
                            </div>
                          </div>
                          <div className="flex max-w-full flex-wrap items-center gap-2">
                            <span className="rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1 text-[11px] font-medium text-[var(--c-text-secondary)]">
                              {relatedApplications.length} apps
                            </span>
                            {address && (
                              <span className="max-w-[280px] truncate rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.04em] text-[var(--c-text-tertiary)]">
                                {address}
                              </span>
                            )}
                          </div>
                        </header>

                        <div className="px-4 py-2 sm:px-5">
                          <div className="divide-y divide-[var(--c-border)]/55">
                      {relatedApplications.map((app, idx) => {
                        const relatedIdentity = applicationIdentityKey(app || {});
                        const isCurrentApp = relatedIdentity === applicationIdentity;
                        const categoryInfo = getCategoryById(app.category);
                        const reference = app.reference_no || app.ref || '-';
                        const qualifiedReference = formatCouncilQualifiedReference(
                          reference,
                          app.source_council || applicationCouncilSlug
                        );
                        const eventDate = app.received_date || app.scraped_date || app.decision_date;
                        const description = app.ai_summary || app.description || app.proposal || 'No description available';
                        const status = app.status || 'Unknown';
                        const lowerStatus = status.toLowerCase();
                        const statusTone = lowerStatus.includes('approved') || lowerStatus.includes('granted')
                          ? { dot: 'bg-[var(--c-success)]', text: 'text-[var(--c-success)]' }
                          : lowerStatus.includes('refused') || lowerStatus.includes('rejected')
                            ? { dot: 'bg-[var(--c-error)]', text: 'text-[var(--c-error)]' }
                            : lowerStatus.includes('await') || lowerStatus.includes('pending')
                              ? { dot: 'bg-[var(--c-accent)]', text: 'text-[var(--c-accent)]' }
                              : { dot: 'bg-[var(--c-text-tertiary)]', text: 'text-[var(--c-text-secondary)]' };
                        return (
                          <article
                            key={(relatedIdentity || idx) + '-rel'}
                            className={`group grid gap-3 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center ${
                              isCurrentApp ? 'relative before:absolute before:left-[-16px] before:top-4 before:bottom-4 before:w-0.5 before:bg-[var(--c-accent)] sm:before:left-[-20px]' : ''
                            }`}
                          >
                            <div className="flex min-w-0 items-start gap-3">
                              <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[var(--c-border)] bg-[var(--c-bg)]">
                                {categoryInfo?.icon ? (
                                  <Image
                                    src={categoryInfo.icon}
                                    alt={categoryInfo.label || ''}
                                    width={30}
                                    height={30}
                                    unoptimized
                                    className="h-7 w-7 object-contain"
                                  />
                                ) : (
                                  <Building className="h-4 w-4 text-[var(--c-text-tertiary)]" />
                                )}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--c-accent)]">
                                    {qualifiedReference}
                                  </span>
                                  <span className="text-[11px] text-[var(--c-text-tertiary)]">
                                    {eventDate ? formatDate(eventDate) : 'Date unavailable'}
                                  </span>
                                  {isCurrentApp && (
                                    <span className="rounded-full border border-[var(--c-accent)]/45 bg-[var(--c-accent)]/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--c-accent)]">
                                      Current
                                    </span>
                                  )}
                                </div>
                                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-[var(--c-text-primary)]">
                                  {description}
                                </p>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[var(--c-text-secondary)]">
                                  <span className={`inline-flex items-center gap-1.5 font-medium ${statusTone.text}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${statusTone.dot}`} />
                                    {status}
                                  </span>
                                  {app.scale_rating != null && (
                                    <span className="font-mono uppercase tracking-[0.04em] text-[var(--c-text-tertiary)]">
                                      Scale {app.scale_rating}/10
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 flex-shrink-0 sm:items-end">
                                {app.link && (
                                  <motion.button
                                    onClick={() => window.open(app.link, '_blank')}
                                    className="planning-modal-source-action inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3 text-xs font-medium text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)] hover:text-[var(--c-accent)]"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    View
                                    <span className="inline xs:hidden">↗</span>
                                  </motion.button>
                                )}
                              </div>
                            </div>
                          </article>
                        );
                      })}
                          </div>
                        </div>
                      </section>
                    </motion.div>
                  )}

              {/* Timeline Tab */}
              {activeTab === 'timeline' && relatedApplications.length > 1 && (
                <motion.div
                  key="timeline"
                  custom={tabDirection}
                  variants={tabPanelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-4 sm:p-6"
                >
                  <section className="overflow-hidden rounded-[22px] border border-[var(--c-border)] bg-[var(--c-surface)]">
                    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--c-border)] bg-[var(--c-bg)]/55 px-4 py-3.5 sm:px-5">
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-accent)]">
                          <Clock className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <h3 className="text-base font-semibold leading-tight text-[var(--c-text-primary)]">
                            Timeline
                          </h3>
                          <p className="mt-1 max-w-xl text-[11px] leading-snug text-[var(--c-text-tertiary)]">
                            Chronological history for this address and its linked planning records.
                          </p>
                        </div>
                      </div>
                      <span className="rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1 text-[11px] font-medium text-[var(--c-text-secondary)]">
                        {relatedApplications.length} events
                      </span>
                    </header>

                    <div className="px-4 py-2 sm:px-5">
                      <div className="relative">
                        <div className="absolute bottom-6 left-[15px] top-6 w-px bg-[var(--c-border)]" />

                    <ul className="divide-y divide-[var(--c-border)]/55">
                      {([...relatedApplications]
                        .sort((a,b) => new Date(a.received_date || a.scraped_date || a.decision_date || 0).getTime() - new Date(b.received_date || b.scraped_date || b.decision_date || 0).getTime())
                      ).map((app, index) => {
                        const relatedIdentity = applicationIdentityKey(app || {});
                        const isCurrentApp = relatedIdentity === applicationIdentity;
                        const status = app.status || 'Unknown';
                        const lower = status.toLowerCase();
                        const eventDate = app.received_date || app.scraped_date || app.decision_date;
                        const categoryInfoLocal = getCategoryById(app.category);
                        const reference = app.reference_no || app.ref || '-';
                        const qualifiedReference = formatCouncilQualifiedReference(
                          reference,
                          app.source_council || applicationCouncilSlug
                        );
                        const description = app.ai_summary || app.description || app.proposal || 'No description available';
                        const statusTone = lower.includes('approved') || lower.includes('granted')
                          ? { dot: 'bg-[var(--c-success)]', text: 'text-[var(--c-success)]' }
                          : lower.includes('refused') || lower.includes('rejected')
                            ? { dot: 'bg-[var(--c-error)]', text: 'text-[var(--c-error)]' }
                            : lower.includes('await') || lower.includes('pending')
                              ? { dot: 'bg-[var(--c-accent)]', text: 'text-[var(--c-accent)]' }
                              : { dot: 'bg-[var(--c-text-tertiary)]', text: 'text-[var(--c-text-secondary)]' };

                        return (
                          <li
                            key={(relatedIdentity || index) + '-tl'}
                            className="relative grid gap-3 py-4 pl-10 sm:grid-cols-[8.5rem_minmax(0,1fr)_auto] sm:items-start"
                          >
                            <span className={`absolute left-[15px] top-5 h-3 w-3 -translate-x-1/2 rounded-full border border-[var(--c-border)] ${
                              isCurrentApp ? 'bg-[var(--c-accent)]' : 'bg-[var(--c-surface)]'
                            }`}>
                              <span className={`absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${isCurrentApp ? 'bg-[var(--c-bg)]' : statusTone.dot}`} />
                            </span>

                            <div className="min-w-0">
                              <div className="font-mono text-[11px] uppercase tracking-[0.04em] text-[var(--c-text-secondary)]">
                                {eventDate ? formatDate(eventDate) : 'Date unavailable'}
                              </div>
                              <div className={`mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium ${statusTone.text}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${statusTone.dot}`} />
                                {status}
                              </div>
                            </div>

                            <div className="min-w-0">
                              <div className="flex items-start gap-2">
                                {categoryInfoLocal?.icon && (
                                  <Image
                                    src={categoryInfoLocal.icon}
                                    alt={categoryInfoLocal.label}
                                    width={20}
                                    height={20}
                                    unoptimized
                                    className="mt-0.5 h-6 w-6 flex-shrink-0 object-contain opacity-80"
                                  />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                                    <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--c-accent)]">
                                      {qualifiedReference}
                                    </span>
                                    {isCurrentApp && (
                                      <span className="rounded-full border border-[var(--c-accent)]/45 bg-[var(--c-accent)]/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--c-accent)]">
                                        Current
                                      </span>
                                    )}
                                    {app.scale_rating != null && (
                                      <span className="font-mono text-[10px] uppercase tracking-[0.04em] text-[var(--c-text-tertiary)]">Scale {app.scale_rating}/10</span>
                                    )}
                                    {(app.documents && app.documents.length > 0) && (
                                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.04em] text-[var(--c-text-tertiary)]">
                                        <FileText className="h-3 w-3" />
                                        <span className="hidden xs:inline">{app.documents.length} docs</span>
                                        <span className="inline xs:hidden">{app.documents.length}</span>
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-[var(--c-text-primary)]">
                                    {description}
                                  </p>
                                </div>
                              </div>
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                  {app.link && (
                                    <motion.button
                                      onClick={() => window.open(app.link, '_blank')}
                                      className="planning-modal-source-action inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3 text-xs font-medium text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)] hover:text-[var(--c-accent)]"
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" />
                                      View
                                      <span className="inline xs:hidden">↗</span>
                                    </motion.button>
                                  )}
                                </div>
                              </div>
                          </li>
                        );
                      })}
                    </ul>
                      </div>
                    </div>
                  </section>
                </motion.div>
              )}

              {/* Tags Tab */}
              {activeTab === 'signals' && (
                <motion.div
                  key="signals"
                  custom={tabDirection}
                  variants={tabPanelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="space-y-4"
                >
                  <PlanningSignalsEvidenceTab
                    application={application}
                    category={categoryInfo}
                  />
                </motion.div>
              )}

              {/* Discussion Tab */}
              {discussionsEnabled && activeTab === 'discussion' && (
                <motion.div
                  key="discussion"
                  custom={tabDirection}
                  variants={tabPanelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-4 sm:p-6"
                >
                  <PlanningDiscussionPanel
                    reference={safeReferenceNo}
                    council={applicationCouncilSlug}
                    councilUrl={(application && application.link && application.link !== '#') ? application.link : undefined}
                  />
                </motion.div>
              )}

              {/* Journal Tab */}
              {normalizedPublishedArticle && activeTab === 'journal' && (
                <motion.div
                  key="journal"
                  custom={tabDirection}
                  variants={tabPanelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-4 sm:p-6"
                >
                  <article className="mx-auto max-w-3xl space-y-5">
                    <div className="pr-record-card">
                      <div className="pr-kicker pr-kicker--accent">
                        City Scope Journal
                      </div>
                      <h3 className="mt-2 font-['Newsreader'] text-2xl font-medium leading-tight tracking-[-0.01em] text-[var(--c-text-primary)]">
                        {normalizedPublishedArticle.title}
                      </h3>
                      {normalizedPublishedArticle.excerpt && (
                        <p className="mt-3 text-sm leading-relaxed text-[var(--c-text-secondary)]">
                          {normalizedPublishedArticle.excerpt}
                        </p>
                      )}
                      <div className="mt-4 flex flex-wrap gap-2 text-xs text-[var(--c-text-secondary)]">
                        {normalizedPublishedArticle.publishedAt && (
                          <span className="rounded-md border border-[var(--c-border)] px-2 py-1">
                            {formatDate(normalizedPublishedArticle.publishedAt)}
                          </span>
                        )}
                        {normalizedPublishedArticle.readingTime && (
                          <span className="rounded-md border border-[var(--c-border)] px-2 py-1">
                            {normalizedPublishedArticle.readingTime} min read
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="pr-record-card space-y-4">
                      {(normalizedPublishedArticle.sections || []).map((section, index) => {
                        if (section.type === 'heading') {
                          return (
                            <h4 key={index} className="pt-1 text-lg font-semibold text-[var(--c-text-primary)]">
                              {renderInlineMarkdown(section.text)}
                            </h4>
                          );
                        }
                        if (section.type === 'list' || section.type === 'bullets') {
                          return (
                            <ul key={index} className="space-y-2">
                              {section.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex gap-2 text-sm leading-relaxed text-[var(--c-text-secondary)]">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--c-accent)]" />
                                  <span>{renderInlineMarkdown(item)}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        if (section.type === 'callout') {
                          return (
                            <div key={index} className="border border-[var(--c-accent)]/30 bg-[rgba(var(--c-accent-rgb),0.06)] p-4 text-sm leading-relaxed text-[var(--c-text-primary)]">
                              {renderInlineMarkdown(section.text)}
                            </div>
                          );
                        }
                        if (section.type === 'divider') {
                          return <hr key={index} className="border-[var(--c-border)]" />;
                        }
                        return (
                          <p key={index} className="text-sm leading-relaxed text-[var(--c-text-secondary)]">
                            {renderInlineMarkdown(section.text)}
                          </p>
                        );
                      })}
                    </div>

                    <a
                      href={`/blog/${normalizedPublishedArticle.slug}`}
                      className="pr-button inline-flex items-center gap-2"
                    >
                      Open in Journal
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </article>
                </motion.div>
              )}

              {/* Reports Tab */}
              {!sourceRecordOnly && activeTab === 'reports' && (
                <motion.div
                  key="reports"
                  custom={tabDirection}
                  variants={tabPanelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-6 space-y-4"
                >
                  {/* Application details summary */}
                  <div className="pr-record-card">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="pr-kicker">Application summary</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="break-words"><span className="text-[var(--c-text-secondary)]">Location:</span> <span className="ml-2 text-[var(--c-text-primary)]">{address}</span></div>
                      <div><span className="text-[var(--c-text-secondary)]">Status:</span> <span className="ml-2 text-[var(--c-text-primary)]">{(application && application.status) || 'Unknown'}</span></div>
                      <div><span className="text-[var(--c-text-secondary)]">Category:</span> <span className="ml-2 text-[var(--c-accent)]">{categoryInfo?.label || 'N/A'}</span></div>
                      <div><span className="text-[var(--c-text-secondary)]">Impact Scale:</span> <span className="ml-2 text-[var(--c-text-primary)]">{application?.scale_rating ? `${application.scale_rating}/10` : '—'}</span></div>
                      {isWorthWatching && (
                        <div className="sm:col-span-2">
                          <span className="text-[var(--c-text-secondary)]">Worth watching:</span>
                          <span className="ml-2 inline-flex max-w-full items-center gap-1.5 border border-[var(--c-accent)]/35 bg-[rgba(var(--c-accent-rgb),0.08)] px-2.5 py-1 text-[12px] font-medium leading-none text-[var(--c-accent)] align-middle">
                            <Flag className="h-3 w-3 shrink-0" />
                            <span className="min-w-0 truncate">{application?.interest_reason || 'Selected by the CityScope interest layer'}</span>
                          </span>
                        </div>
                      )}
                      {documents && documents.length > 0 && (
                        <div className="sm:col-span-2"><span className="text-[var(--c-text-secondary)]">Documents:</span> <span className="ml-2 text-[var(--c-text-primary)]">{documents.length} supporting document{documents.length !== 1 ? 's' : ''} available</span></div>
                      )}
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[var(--c-text-secondary)]">Description</span>
                          {showAiSummaries && application.ai_summary && (
                            <div className="px-1.5 py-0.5 bg-[var(--c-accent)]/20 text-[var(--c-accent)] rounded text-xs font-medium">
                              AI
                            </div>
                          )}
                        </div>
                        {application?.ai_summary && description && (
                          <motion.button
                            onClick={() => setShowOriginalReport((v) => !v)}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs text-[var(--c-accent)] hover:text-[var(--c-accent)]/80 border border-[var(--c-accent)]/30 rounded hover:bg-[var(--c-accent)]/10 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {showOriginalReport ? 'Hide' : 'Original'}
                          </motion.button>
                        )}
                      </div>

                      {/* Hidden original description panel */}
                      {application?.ai_summary && description && showOriginalReport && (
                        <div className="mb-2 p-2 border border-[var(--c-border)] rounded bg-[var(--c-surface)]/40 backdrop-blur-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--c-text-secondary)]"></div>
                            <span className="text-xs font-mono text-[var(--c-text-secondary)]">ORIGINAL</span>
                          </div>
                          <p className="text-xs text-[var(--c-text-primary)] leading-relaxed whitespace-pre-wrap">{description}</p>
                        </div>
                      )}

                      <p className="text-[var(--c-text-primary)] leading-relaxed text-sm break-words whitespace-pre-wrap">{displayDescription}</p>
                    </div>

                    {!generatedReport && !isGeneratingReport && !reportError && (
                      <button
                        type="button"
                        onClick={generateReport}
                        className="pr-button mt-4 w-full justify-center"
                      >
                        <FileText className="h-4 w-4" />
                        Generate report
                      </button>
                    )}
                  </div>

                  {/* Loading/Error/Generated Report Display */}
                  {isGeneratingReport && (
                    <div className="pr-record-card text-center">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="mx-auto mb-2 h-8 w-8 rounded-full border-2 border-[var(--c-accent)] border-t-transparent"
                      />
                      <p className="text-[var(--c-text-secondary)]">Generating AI report...</p>
                    </div>
                  )}

                  {reportError && (
                    <div className="pr-record-card border-[var(--c-error)]/30 bg-[rgba(var(--c-error-rgb,180,90,42),0.06)]">
                      <p className="text-sm text-red-400">Error: {reportError}</p>
                    </div>
                  )}

                  {generatedReport && (
                    <div className="pr-record-card space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-[var(--c-text-primary)]">Generated Report</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => copyReport(generatedReport)}
                            className="px-3 py-1 text-xs border border-[var(--c-border)] rounded text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
                          >
                            {copiedReport ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </button>
                          <button
                            onClick={() => downloadReport(generatedReport, `planning-report-${safeReferenceNo}`)}
                            className="px-3 py-1 text-xs border border-[var(--c-border)] rounded text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
                          >
                            <Download className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {reportPersistenceNotice && (
                        <div className={`rounded-md border px-3 py-2 text-xs ${
                          reportPersistenceNotice.syncState === 'account'
                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                            : reportPersistenceNotice.syncState === 'failed'
                              ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                              : 'border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)]'
                        }`}>
                          {reportPersistenceNotice.message}
                        </div>
                      )}
                      <div className="max-h-60 overflow-auto border border-[var(--c-border)] rounded p-3 text-[var(--c-text-primary)] whitespace-pre-wrap text-sm">
                        {generatedReport}
                      </div>
                    </div>
                  )}

                  {/* Previous Reports Section */}
                  <div className="pr-record-card">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="font-medium text-[var(--c-text-primary)]">Previous Reports</h4>
                      <span className="text-xs text-[var(--c-text-secondary)]">{previousReports.length} saved</span>
                    </div>
                    {previousReports.length === 0 ? (
                      <div className="flex flex-col items-center gap-1.5 rounded-xl border border-dashed border-[var(--c-border)] bg-[var(--c-surface)]/40 px-4 py-6 text-center">
                        <FileText className="h-5 w-5 text-[var(--c-text-tertiary)]" />
                        <p className="text-sm font-medium text-[var(--c-text-primary)]">No previous reports yet</p>
                        <p className="max-w-[280px] text-[11px] text-[var(--c-text-tertiary)]">
                          Generate an AI-powered report above and it will be saved here for future reference.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {previousReports.map(r => (
                          <div key={r.id} className="border border-[var(--c-border)] rounded-md p-3">
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-[var(--c-text-secondary)]">{new Date(r.generated_at).toLocaleString('en-GB')}</div>
                              <div className="flex items-center gap-2">
                                <button onClick={() => setSelectedPrevReportId(selectedPrevReportId === r.id ? null : r.id)} className="px-2 py-1 text-xs border border-[var(--c-border)] rounded-md text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]">
                                  <Eye className="w-4 h-4 inline" /> {selectedPrevReportId === r.id ? 'Hide' : 'View'}
                                </button>
                                <button onClick={() => copyReport(r.report)} className="px-2 py-1 text-xs border border-[var(--c-border)] rounded-md text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]">
                                  <Copy className="w-4 h-4 inline" /> Copy
                                </button>
                                <button onClick={() => downloadReport(r.report, `planning-report-${r.reference_no}`)} className="px-2 py-1 text-xs border border-[var(--c-border)] rounded-md text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]">
                                  <Download className="w-4 h-4 inline" /> TXT
                                </button>
                                <button onClick={() => downloadPDF(r.report, `planning-report-${r.reference_no}`)} className="px-2 py-1 text-xs border border-[var(--c-border)] rounded-md text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]">
                                  <Download className="w-4 h-4 inline" /> PDF
                                </button>
                                <button onClick={() => deletePrevReport(r.id)} className="px-2 py-1 text-xs border border-red-500/30 rounded-md text-red-400 hover:text-red-300">
                                  <Trash2 className="w-4 h-4 inline" />
                                </button>
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-[var(--c-text-secondary)] line-clamp-3">{r.report}</div>
                            {selectedPrevReportId === r.id && (
                              <div className="mt-2 max-h-60 overflow-auto border border-[var(--c-border)] rounded-md p-2 text-[var(--c-text-primary)] whitespace-pre-wrap">
                                {r.report}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (documentsByApp.length > 0 || sourceRecordOnly) && (
                <motion.div
                  key="documents"
                  custom={tabDirection}
                  variants={tabPanelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-6"
                >
                  <div className="space-y-6">
                    {documentListNotice && <p role="status" className="text-sm text-[var(--c-text-secondary)]">{documentListNotice}</p>}
                    {sourceRecordOnly && documentsByApp.length===0 && application?.link && (
                      <a href={application.link} target="_blank" rel="noopener noreferrer" className="text-sm text-[var(--c-accent)]">View council record</a>
                    )}
                    {documentsByApp.map(({ app, docs }, groupIdx) => (
                      <motion.button
                        type="button"
                        onClick={() => {
                          trackApplicationDetailAction('document_thumbnail_opened', {
                            placement: 'documents_group',
                            metadata: { document_count: docs.length },
                          });
                          setDocDirection(1);
                          setPreviewDocs(docs);
                          setCurrentDocIndex(0);
                        }}
                        key={(applicationIdentityKey(app || {}) || groupIdx) + '-docs'}
                        className="w-full text-left p-3 rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)] hover:border-[var(--c-accent)]/40 transition-colors"
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.995 }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded font-mono text-xs bg-[var(--c-surface)] border border-[var(--c-border)] text-[var(--c-text-secondary)]">
                              {app.reference_no || app.ref || '—'}
                            </span>
                            <span className="text-xs text-[var(--c-text-secondary)]">{formatDate(app.received_date || app.scraped_date)}</span>
                          </div>
                          {app.link && (
                            <a
                              href={app.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(event) => {
                                event.stopPropagation();
                                trackApplicationDetailAction('council_record_opened', {
                                  placement: 'documents_tab',
                                  metadata: { destination: 'council_portal' },
                                });
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2.5 py-1 text-[11px] font-medium text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)]/40 hover:bg-[var(--c-accent)]/10 hover:text-[var(--c-accent)]"
                            >
                              <ExternalLink className="h-3 w-3" />
                              View application
                            </a>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {docs.map((doc, index) => (
                            <motion.div
                              key={(doc.thumbnail_url || getDocumentOpenUrl(doc) || index) + '-doc'}
                              className={`pr-doc-thumb cursor-pointer ${currentDocIndex === index ? 'is-active' : ''}`}
                              whileHover={{ scale: 1.02 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                 trackApplicationDetailAction('document_thumbnail_opened', {
                                   placement: 'documents_grid',
                                  metadata: {
                                    document_index: index + 1,
                                    has_document_url: Boolean(getDocumentOpenUrl(doc)),
                                  },
                                });
                                setDocDirection(1);
                                setPreviewDocs(docs);
                                setCurrentDocIndex(index);
                              }}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.03, duration: 0.25 }}
                            >
                              <Image
                                src={getDocumentPreviewUrl(doc)}
                                alt={`Document ${index + 1}`}
                                fill
                                className="object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div title={doc.title || doc.source_desc || doc.doc_type} className="absolute bottom-1 right-1 max-w-[95%] truncate bg-black/60 text-white text-xs px-1 py-0.5 rounded">
                                {sourceRecordOnly ? `${doc.title || doc.source_desc || doc.doc_type || 'Document'} · ${(doc.format || '').toUpperCase()}` : index + 1}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
              </motion.div>
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>
          <AnimatePresence>
            {showWeeklyReportsPrompt && (
              <motion.aside
                ref={reportOfferRef}
                key="weekly-reports-application-prompt"
                aria-label="Weekly Reports sign up"
                aria-live="polite"
                className="pointer-events-none absolute inset-x-3 bottom-3 z-40 flex justify-center sm:inset-x-auto sm:right-4 sm:bottom-4"
                initial={{ opacity: 0, y: 18, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.98 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="pointer-events-auto w-full max-w-[410px] border border-[var(--c-border)] border-t-2 border-t-[var(--c-text-primary)] bg-[var(--c-surface)] text-[var(--c-text-primary)] shadow-[0_16px_36px_rgba(0,0,0,0.18)]">
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-[3px] border border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-accent)]">
                          <Mail className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                          <p className="pr-section-label mb-2">Weekly reports</p>
                          <h3 className="font-['Newsreader'] text-[21px] leading-[1.05] text-[var(--c-text-primary)]">
                            Keep track without reopening every application
                          </h3>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => dismissWeeklyReportsPrompt('later')}
                        className="pr-button pr-button--ghost !h-9 !w-9 flex-none !p-0"
                        aria-label="Close weekly reports sign up prompt"
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                    <p className="mt-3 border-l-2 border-[var(--c-accent)] pl-3 text-sm leading-6 text-[var(--c-text-secondary)]">
                      A concise Monday digest of notable planning applications and decisions in your area.
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-[var(--c-border)] pt-4">
                      <a
                        href={reportSignupPromptUrl}
                        onClick={() => {
                          trackCoreJourneyEvent('reports', 'report_signup_started', {
                            source: 'application_detail_context',
                            placement: `application_${activeTab}_panel`,
                            entityType: 'planning_application',
                            entityId: safeReferenceNo,
                          });
                          dismissWeeklyReportsPrompt('later');
                        }}
                        className="pr-button"
                      >
                        Get weekly reports
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                      <button
                        type="button"
                        onClick={() => dismissWeeklyReportsPrompt('never')}
                        className="pr-textlink text-xs"
                      >
                        Never show me this again
                      </button>
                    </div>
                  </div>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  if (renderInline) {
    return modal;
  }

  if (!hasMounted) {
    return null;
  }

  return createPortal(modal, document.body);
};

export default PlanningApplicationModal;
